/**
 * Tiny i18n: a flat string map per locale + a `useT()` hook + a
 * persistent language toggle. No external library - the app is small
 * enough that a 200-line dictionary plus one Zustand store covers
 * everything customer-facing without pulling 30 KB of react-i18next.
 *
 * Convention:
 *   - keys are dotted: `nav.home`, `orders.title`, `balance.cta`
 *   - missing keys fall back to English; missing in English too
 *     return the key itself so it's obvious where translations were
 *     forgotten.
 *   - the switcher saves the choice in localStorage under `ns-lang`
 *     so it survives reloads. First-time visitors get RU if their
 *     browser language starts with `ru`, otherwise EN.
 *   - admin pages are kept English-only on purpose: they're operator
 *     surfaces, not customer-facing.
 */

import { create } from "zustand";

export type Lang = "en" | "ru";

const STORAGE_KEY = "ns-lang";

function detectLang(): Lang {
  if (typeof window === "undefined") return "en";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "en" || saved === "ru") return saved;
  } catch {
    /* localStorage blocked - fall through */
  }
  const nav =
    typeof navigator !== "undefined" && navigator.language
      ? navigator.language.toLowerCase()
      : "";
  return nav.startsWith("ru") ? "ru" : "en";
}

interface LangStore {
  lang: Lang;
  setLang: (l: Lang) => void;
}

export const useLang = create<LangStore>((set) => ({
  lang: detectLang(),
  setLang: (l) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* localStorage blocked */
    }
    set({ lang: l });
  },
}));

const EN: Record<string, string> = {
  // ---- nav / layout ----
  "nav.home": "Home",
  "nav.explore": "Explore",
  "nav.steam_gifts": "Steam Gifts",
  "nav.steam_topup": "Steam Top-up",
  "nav.direct_topup": "Direct Top-up",
  "nav.orders": "Orders",
  "nav.transactions": "Transactions",
  "nav.balance": "Balance",
  "nav.profile": "Profile",
  "nav.shop": "Shop",
  "nav.account": "Account",
  "nav.marketplaces": "Marketplaces",
  "nav.admin": "Admin",
  "nav.api_playground": "API Playground",
  "nav.logout": "Log out",
  "nav.greeting.morning": "Good morning",
  "nav.greeting.afternoon": "Good afternoon",
  "nav.greeting.evening": "Good evening",
  "nav.greeting.night": "Good night",

  // ---- home ----
  "home.subtitle": "Here's a snapshot of your account today.",
  "home.kpi.balance": "Balance",
  "home.kpi.balance.live": "USD · live",
  "home.kpi.orders": "Total orders",
  "home.kpi.orders.alltime": "All time",
  "home.kpi.last_topup": "Last top-up",
  "home.kpi.last_topup.none": "No top-ups yet",
  "home.kpi.account": "Account",
  "home.kpi.recent": "Recent activity",
  "home.recent.orders": "Recent orders",
  "home.recent.transactions": "Recent transactions",
  "home.recent.movements": "Balance movements",
  "home.recent.no_orders": "No orders yet.",
  "home.recent.no_transactions": "No transactions yet.",
  "home.recent.start_exploring": "Start exploring →",
  "home.recent.view_all": "View all →",
  "home.greeting.fallback": "there",
  "home.quick.title": "Quick actions",
  "home.quick.topup_steam": "Top up Steam",
  "home.quick.buy_gift": "Buy a Steam gift",
  "home.quick.explore_catalog": "Browse catalog",
  "home.action.explore": "Explore catalog",
  "home.action.explore.desc":
    "Every gift card, top-up, and code in one search.",
  "home.action.steam_gifts": "Steam Gifts",
  "home.action.steam_gifts.desc":
    "Send any Steam game to a friend in any region.",
  "home.action.steam_topup": "Steam Top-up",
  "home.action.steam_topup.desc":
    "Add funds to a Steam account in RUB / KZT / UAH.",
  "home.action.balance": "Top up balance",
  "home.action.balance.desc": "Bybit transfer or TON deposit - instant credit.",

  // ---- orders ----
  "orders.title": "Orders",
  "orders.subtitle": "Your purchase history. Click a row for delivered codes / status.",
  "orders.filter.status": "Status",
  "orders.filter.custom_id": "Custom ID",
  "orders.filter.account_id": "Account ID",
  "orders.filter.account_id_placeholder": "Steam login of the recipient...",
  "orders.filter.custom_id_placeholder": "UUID...",
  "orders.col.date": "Date",
  "orders.col.product": "Product",
  "orders.col.qty": "Qty",
  "orders.col.total": "Total",
  "orders.col.status": "Status",
  "orders.col.custom_id": "Custom ID",
  "orders.empty": "No orders.",
  "orders.loading": "Loading...",
  "orders.steam_topup_label": "Steam Top-up",
  "orders.status.all": "All",
  "orders.status.new": "New",
  "orders.status.in_progress": "In progress",
  "orders.status.completed": "Completed",
  "orders.status.refunded": "Refunded",
  "orders.status.canceled": "Canceled",
  "orders.page": "Page",
  "orders.page.of": "of",
  "orders.page.total": "total",
  "orders.page.prev": "← Prev",
  "orders.page.next": "Next →",

  // ---- order detail ----
  "order.title": "Order",
  "order.field.status": "Status",
  "order.field.date": "Date",
  "order.field.product": "Product",
  "order.field.quantity": "Quantity",
  "order.field.total": "Total",
  "order.field.codes": "Delivered codes",
  "order.field.payload": "Data",
  "order.copy": "Copy",
  "order.copy_all": "Copy all",
  "order.copied": "Copied ✓",
  "order.copy_failed": "Copy failed",
  "order.copy_all_failed": "Copy-all failed",
  "order.download_txt": "Download .txt",
  "order.back": "← Orders",
  "order.not_found": "Order not found.",

  // ---- transactions ----
  "tx.title": "Transactions",
  "tx.subtitle": "Every balance-changing event tied to your account.",
  "tx.filter.type": "Type",
  "tx.filter.custom_id": "Custom ID",
  "tx.filter.from": "From",
  "tx.filter.to": "To",
  "tx.filter.custom_id_placeholder": "UUID...",
  "tx.col.date": "Date",
  "tx.col.type": "Type",
  "tx.col.custom_id": "Custom ID",
  "tx.col.amount": "Amount",
  "tx.col.balance": "Balance",
  "tx.col.balance_after": "Balance after",
  "tx.col.reference": "Reference",
  "tx.empty": "No transactions match these filters.",
  "tx.type.all": "All",
  "tx.type.topup": "Top-up",
  "tx.type.purchase": "Purchase",
  "tx.type.refund": "Refund",
  "tx.type.deposit": "Deposit",
  "tx.type.withdraw": "Withdraw",
  "tx.type.admin_topup": "Manual credit",
  "tx.type.admin_debit": "Manual debit",
  "tx.col.details": "Details",
  "tx.detail.balance_before": "Balance before",
  "tx.detail.balance_after": "Balance after",
  "tx.detail.reason": "Reason",
  "tx.export": "Export",
  "tx.export.csv": "CSV",
  "tx.export.xlsx": "Excel",
  "tx.export.busy": "Exporting...",
  "tx.export.error": "Export failed. Please try again.",

  // ---- balance ----
  "balance.title": "Balance",
  "balance.subtitle": "Top up via Bybit internal transfer or TON network.",
  "balance.available": "Available",
  "balance.live": "live · refreshes every 15 s",
  "balance.tab.bybit": "Bybit",
  "balance.tab.bybit.sub": "Internal transfer",
  "balance.tab.ton": "TON",
  "balance.tab.ton.sub": "USDT only",
  "balance.bybit.title": "Bybit internal transfer",
  "balance.bybit.intro":
    "USDT only. Send funds via Bybit's Internal Transfer feature - fee-free between Bybit accounts and credited within a few minutes.",
  "balance.bybit.step1": "Save your sender Bybit UID",
  "balance.bybit.step1.desc":
    "The UID of your Bybit account. Top-ups are attributed to your balance only when the transfer comes from this exact UID.",
  "balance.bybit.step2": "Send to our Bybit UID",
  "balance.bybit.copy_label": "Operator UID",
  "balance.bybit.copy_note":
    "Bybit → Assets → Internal Transfer. Send USDT from the same account whose UID you saved in step 1.",
  "balance.bybit.no_uid_notice":
    "Save your sender UID in step 1 first - until then we hide the operator UID, and any incoming transfer can't be auto-credited anyway.",
  "balance.bybit.uid_label": "Bybit UID",
  "balance.bybit.uid_placeholder": "e.g. 12345678",
  "balance.bybit.uid_required": "UID is required",
  "balance.bybit.set_uid": "Set UID",
  "balance.bybit.change": "Change",
  "balance.bybit.not_set": "not set",
  "balance.bybit.uid_saved": "Sender UID saved",
  "balance.bybit.confirm.title": "Confirm sender UID change",
  "balance.bybit.confirm.desc":
    "Changing this controls whose balance gets credited - confirm with your authenticator.",
  "balance.bybit.save_failed": "Save failed",
  "balance.bybit.save_failed.body": "Could not save (wrong password?)",
  "balance.password.current": "Current password",
  "balance.wrong_code": "Wrong code - try again.",
  "balance.ton.title": "TON network",
  "balance.ton.unconfigured":
    "TON deposits are not available right now - operator's wallet address isn't configured. Use Bybit instead, or contact support.",
  "balance.ton.intro":
    "For users who don't have a Bybit account. Send USDT on the TON network - any other token will not be credited.",
  "balance.ton.warning":
    "Send only USDT on TON. Native TON coin, NOT, jUSDT from other chains, or any other jetton will be lost - the watcher only matches USDT-jetton transfers.",
  "balance.ton.step1": "Generate your TON memo",
  "balance.ton.step1.desc":
    "A short random tag that tells the watcher which balance to credit. The memo is public on-chain, so it's deliberately opaque - no username, email or anything that could identify you.",
  "balance.ton.step2": "Send USDT to our TON address",
  "balance.ton.copy_label": "Wallet address",
  "balance.ton.copy_note":
    "Use a TON wallet that supports jettons (Tonkeeper, MyTonWallet, Bybit Web3, etc.). Set the asset to USDT, not TON. Paste the memo into the comment / memo field of the transfer.",
  "balance.ton.no_memo_notice":
    "Generate a memo in step 1 first - without it the deposit can't be credited automatically.",
  "balance.ton.not_generated": "not generated",
  "balance.ton.copy": "Copy",
  "balance.ton.copied": "Copied ✓",
  "balance.ton.generate_new": "Generate new",
  "balance.ton.generate": "Generate",
  "balance.ton.rotate_btn": "Rotate memo",
  "balance.ton.generate_btn": "Generate memo",
  "balance.ton.generating": "Generating...",
  "balance.ton.confirm.rotate.title": "Confirm memo rotation",
  "balance.ton.confirm.rotate.desc":
    "Rotating the memo invalidates any pending TON transfer that still references the old one - confirm with your authenticator.",
  "balance.ton.confirm.create.title": "Confirm memo creation",
  "balance.ton.confirm.create.desc":
    "Generating a TON memo - confirm with your authenticator.",
  "balance.ton.rotated": "TON memo rotated",
  "balance.ton.generated": "TON memo generated",
  "balance.ton.rotate_warning":
    "Rotating replaces the current memo. Any pending TON transfer using the old memo will arrive unattributed and need manual handling.",
  "balance.ton.could_not_generate": "Could not generate (wrong password?)",
  "balance.ton.generation_failed": "Generation failed",
  "balance.copy_failed": "Copy failed",
  "balance.saving": "Saving...",

  // ---- steam topup ----
  "steam_topup.title": "Steam Top-up",
  "steam_topup.subtitle":
    "Direct top-up of your Steam wallet. Enter the login and how many USD to send.",
  "steam_topup.login_label": "Steam login or email",
  "steam_topup.login_placeholder": "your_steam_login",
  "steam_topup.amount_label": "Amount, USD",
  "steam_topup.amount_placeholder": "e.g. 5.00",
  "steam_topup.cta": "Top up",
  "steam_topup.cta_busy": "Sending...",
  "steam_topup.invalid_login": "Invalid Steam login",
  "steam_topup.invalid_login_body": "Use either your Steam username or the email tied to it.",
  "steam_topup.invalid_amount": "Invalid amount",
  "steam_topup.completed": "Top-up completed",
  "steam_topup.in_progress": "Top-up in progress",
  "steam_topup.refunded": "Top-up refunded",
  "steam_topup.insufficient": "Insufficient balance",
  "steam_topup.view_order": "View order",

  // ---- direct top-up (Giftery recharge_fixed: PUBG, Delta Force, ...) ----
  "direct_topup.title": "Direct Top-up",
  "direct_topup.subtitle":
    "Top up your account directly - pick a product, enter your ID and amount.",
  "direct_topup.empty":
    "No direct top-up products are available right now. Check back soon.",
  "direct_topup.search": "Search by game name",
  "direct_topup.choose_amount": "Pick a package",
  "direct_topup.buy": "Buy",
  "direct_topup.buying": "Buying...",
  "direct_topup.completed": "Top-up completed",
  "direct_topup.in_progress": "Top-up in progress",
  "direct_topup.refunded": "Top-up refunded",
  "direct_topup.insufficient": "Insufficient balance",
  "direct_topup.no_fields": "No fields to fill in.",
  "direct_topup.required": "Required",
  "direct_topup.invalid_value": "Invalid value",
  "direct_topup.back": "Back to all top-ups",

  // ---- steam gifts ----
  "steam_gift.title": "Steam Gifts",
  "steam_gift.subtitle":
    "Find a Steam game by name or App ID, then send it as a gift via friend invite link.",
  "steam_gift.unconfigured":
    "The Steam Gift service isn't configured on this account. Contact your manager.",
  "steam_gift.search.placeholder":
    "Search by name or App ID (e.g. 730, 'Counter-Strike')...",
  "steam_gift.search.start": "Start typing to search the Steam catalogue.",
  "steam_gift.search.busy": "Searching...",
  "steam_gift.search.failed": "Search failed.",
  "steam_gift.search.empty": "No games match \"{q}\".",
  "steam_gift.no_image": "no image",
  "steam_gift.app": "App",
  "steam_gift.detail.loading": "Loading details...",
  "steam_gift.detail.no_subs": "No purchasable packages for this game.",
  "steam_gift.region_label": "Region",
  "steam_gift.app_label": "Game",
  "steam_gift.friend_label": "Steam friend invite link",
  "steam_gift.friend_placeholder": "https://s.team/p/abc-defg/h12345",
  "steam_gift.gift_title_label": "Gift title",
  "steam_gift.gift_description_label": "Description",
  "steam_gift.gift_description_placeholder": "optional",
  "steam_gift.youll_pay": "You'll pay",
  "steam_gift.pick_region": "Pick a region in the table.",
  "steam_gift.cta": "Buy",
  "steam_gift.cta_busy": "Sending...",

  // ---- catalog / category ----
  "catalog.title": "Catalog",
  "catalog.col.service": "Service",
  "catalog.col.price": "Price",
  "catalog.col.in_stock": "In stock",
  "catalog.col.qty": "Qty",
  "catalog.col.action": "Action",
  "catalog.buy": "Buy",
  "catalog.buy_all": "Buy all",
  "catalog.buy_busy": "Buying...",
  "catalog.in_stock": "in stock",
  "catalog.out_of_stock": "out of stock",
  "catalog.out": "out",
  "catalog.no_services": "No services in this category.",
  "catalog.bulk.set_qty": "Set quantities, then buy individually or all at once.",
  "catalog.bulk.selected": "selected",
  "catalog.bulk.total": "total",
  "catalog.unsupported_fields":
    "This category requires extra fields beyond a quantity. Inline buying isn't available here yet - contact a manager.",
  "catalog.back": "← Catalog",

  "menu.back": "← Catalog",
  "menu.not_found": "Menu not found.",
  "menu.empty": "No categories yet.",
  "explore.title": "Catalog",
  "explore.subtitle.search":
    "Categories matching your query (by name or by service).",
  "explore.subtitle.browse":
    "Pick a menu to drill into its categories - or start typing to search.",
  "explore.search.placeholder": "Search categories or services...",
  "explore.empty": "Nothing matches your search.",
  "explore.failed": "Failed to load catalog.",
  "explore.menus.one": "menu",
  "explore.menus.many": "menus",
  "explore.categories.one": "category",
  "explore.categories.many": "categories",
  "explore.services.one": "service",
  "explore.services.many": "services",
  "explore.match": "match",

  // ---- profile ----
  "profile.title": "Profile",
  "profile.subtitle": "Account, security, and active sessions.",
  "profile.field.login": "Login",
  "profile.field.display_name": "Display name",
  "profile.field.balance": "Balance",
  "profile.field.rights": "Rights",
  "profile.field.rights.none": "none",
  "profile.section.account": "Account",
  "profile.section.password": "Change password",
  "profile.section.password.subtitle":
    "Changing your password signs out every device, including this one.",
  "profile.section.totp": "Two-factor authentication",
  "profile.section.sessions": "Active sessions",
  "profile.section.sessions.subtitle":
    "Browser sessions tied to your account. Sign out of every device including this one with the button on the right.",
  "profile.password.current": "Current password",
  "profile.password.new": "New password",
  "profile.password.confirm": "Confirm new password",
  "profile.password.cta": "Change password",
  "profile.password.busy": "Updating...",
  "profile.password.mismatch": "Passwords do not match",
  "profile.password.changed": "Password changed",
  "profile.password.changed.body":
    "All sessions have been signed out. Please log in again.",
  "profile.password.fail": "Could not change password",
  "profile.totp.enabled": "Enabled",
  "profile.totp.disabled": "Disabled",
  "profile.totp.enable": "Enable",
  "profile.totp.disable": "Disable",
  "profile.totp.subtitle":
    "Use any TOTP authenticator (Google Authenticator, Authy, 1Password, Aegis...). Decide separately whether to require it on login and on every purchase - bulk-buy still asks once per batch.",
  "profile.totp.password_hint":
    "Required so a stolen session cookie can't bind 2FA to a hijacker's authenticator behind your back.",
  "profile.totp.generating": "Generating...",
  "profile.totp.enable_btn": "Enable 2FA",
  "profile.totp.step1.title": "Step 1 - Add to your authenticator",
  "profile.totp.step1.desc":
    "Scan the QR with your app, or copy the secret manually.",
  "profile.totp.secret_label": "Secret (manual)",
  "profile.totp.qr_alt": "QR for authenticator",
  "profile.totp.issuer_note":
    "Issuer: NS Cabinet. Algorithm SHA1, 6 digits, period 30 s.",
  "profile.totp.step2.title": "Step 2 - Confirm a code",
  "profile.totp.confirming": "Confirming...",
  "profile.totp.activate_btn": "Activate",
  "profile.totp.setup_failed": "Setup failed",
  "profile.totp.setup_failed.body": "Wrong password or 2FA already in setup",
  "profile.totp.activated": "2FA enabled",
  "profile.totp.activated.body":
    "Both login and purchase prompts are now on. You can change this below.",
  "profile.totp.wrong_code": "Wrong code",
  "profile.totp.wrong_code.body": "Try the next code your app generates.",
  "profile.totp.disabled_toast": "2FA disabled",
  "profile.totp.cannot_disable": "Cannot disable.",
  "profile.totp.confirm_with_app": "Confirm with your authenticator code.",
  "profile.totp.wrong_code.retry": "Wrong code - try again.",
  "profile.totp.save_failed": "Could not save setting",
  "profile.totp.active": "2FA is active.",
  "profile.totp.toggle.login": "Require code on login",
  "profile.totp.toggle.login.hint": "Asked once per sign-in session.",
  "profile.totp.toggle.purchase": "Require code on purchase",
  "profile.totp.toggle.purchase.hint":
    "Asked once per batch (bulk-buy reuses the same code).",
  "profile.totp.toggle.deposit": "Require code on Bybit UID change",
  "profile.totp.toggle.deposit.hint":
    "Asked when adding, editing, or removing your sender Bybit UID.",
  "profile.totp.action.enable_login": "Enable login 2FA",
  "profile.totp.action.disable_login": "Disable login 2FA",
  "profile.totp.action.enable_purchase": "Enable purchase 2FA",
  "profile.totp.action.disable_purchase": "Disable purchase 2FA",
  "profile.totp.action.enable_deposit": "Enable deposit-change 2FA",
  "profile.totp.action.disable_deposit": "Disable deposit-change 2FA",
  "profile.totp.disable_label": "Disable 2FA",
  "profile.totp.disabling": "Disabling...",
  "profile.totp.disable_btn": "Disable 2FA",
  "profile.sessions.this_device": "this device",
  "profile.sessions.started": "started",
  "profile.sessions.last_seen": "last seen",
  "profile.sessions.empty": "No active sessions.",
  "profile.sessions.revoke_all": "Sign out all",
  "profile.sessions.revoke_all.busy": "Signing out...",
  "profile.sessions.revoked.title": "Signed out everywhere",
  "profile.sessions.revoked.body": "{n} session(s) revoked.",
  "profile.sessions.revoke_failed": "Logout-all failed",
  "profile.logout": "Log out",

  // ---- login ----
  "login.title": "Sign in",
  "login.login_label": "Login",
  "login.password_label": "Password",
  "login.cta": "Sign in",
  "login.cta_busy": "Signing in...",
  "login.error.invalid": "Invalid login or password",
  "login.totp_required": "2FA code required",
  "login.totp_label": "2FA code",
  "login.signup_hint": "Don't have an account?",
  "login.signup_cta": "Sign up",
  "login.back_to_login": "← Back to login",

  // ---- common ----
  "common.loading": "Loading...",
  "common.error": "Error",
  "common.retry": "Retry",
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.close": "Close",
  "common.confirm": "Confirm",
  "common.dash": "-",

  // ---- marketplaces ----
  "mp.loading": "Loading…",
  "mp.back": "← Back to marketplaces",
  "mp.save": "Save",
  "mp.cancel": "Cancel",
  "mp.delete": "Delete",
  "mp.copy": "Copy",
  "mp.copied": "Copied",
  "mp.copy_failed": "Couldn't copy",
  "mp.toast.not_saved": "Couldn't save",

  // hub
  "mp.hub.title": "Marketplaces",
  "mp.hub.subtitle": "Pick a marketplace to manage.",
  "mp.badge.soon": "Coming soon",
  "mp.badge.connected": "Connected",
  "mp.badge.disconnected": "Not connected",
  "mp.hub.card.soon": "Not available yet",
  "mp.hub.card.tabs": "Products · Orders · Settings",

  // product page
  "mp.product.invalid": "There's no such product.",
  "mp.product.title_fallback": "Product",
  "mp.product.intro":
    "Everything for this product is here: link its variants to your services, write buyer instructions, hide a variant, or change the main one.",

  // integration page
  "mp.integration.unknown": "Unknown marketplace.",
  "mp.integration.soon": "{mp} is coming soon — not available yet.",
  "mp.integration.connect_intro":
    "This account isn't connected yet. Connect your seller account to see products, orders and settings. Each account needs its own proxy — all traffic to the marketplace goes through it.",
  "mp.tab.products": "Products",
  "mp.tab.orders": "Orders",
  "mp.tab.settings": "Settings",

  // proxy form
  "mp.proxy.title": "Account proxy",
  "mp.proxy.current": "Current:",
  "mp.proxy.direct": "direct (no proxy)",
  "mp.proxy.help":
    "We test a new proxy by signing in to {mp} before saving it — if it doesn't work, it won't be saved. A working proxy also lifts the auto-pause.",
  "mp.proxy.placeholder": "http://user:pass@host:port  or  socks5://host:port",
  "mp.proxy.save": "Save proxy",
  "mp.proxy.checking": "Checking…",
  "mp.proxy.saved": "Proxy updated and verified",
  "mp.proxy.not_saved": "Proxy not saved",
  "mp.proxy.check_failed": "The new proxy didn't pass the check.",

  // api-key rotation form
  "mp.apikey.title": "API key",
  "mp.apikey.help":
    "Rotate the {mp} API key without disconnecting the account — product links stay intact. We verify the new key by signing in to {mp} before saving; a bad key won't be saved.",
  "mp.apikey.placeholder": "New API key",
  "mp.apikey.save": "Save key",
  "mp.apikey.checking": "Checking…",
  "mp.apikey.saved": "API key updated and verified",
  "mp.apikey.not_saved": "API key not saved",
  "mp.apikey.check_failed": "The new API key didn't pass the check.",

  // settings · proxy down
  "mp.proxydown.title": "Account auto-paused: proxy unreachable",
  "mp.proxydown.desc":
    "Requests to {mp} are paused — we don't keep hitting a dead proxy, and nothing goes out from our own IP. Fix the proxy and press the button: we'll re-check it and switch the account back on.",
  "mp.proxydown.recheck": "Re-check proxy and resume",
  "mp.proxydown.busy": "Checking…",
  "mp.proxydown.resumed": "Account is active again (proxy OK)",
  "mp.proxydown.still_down": "Proxy is still unreachable",

  // settings · fields + actions
  "mp.field.status": "Status",
  "mp.field.proxy": "Proxy",
  "mp.field.connected": "Connected",
  "mp.field.last_sync": "Last sync",
  "mp.field.never": "never",
  "mp.field.direct": "direct",
  "mp.disconnect.confirm":
    "Disconnect the {mp} account (seller_id={sid})? Its synced products and mappings will be removed too.",
  "mp.disconnect.done": "Account disconnected",
  "mp.disconnect.failed": "Couldn't disconnect",
  "mp.disconnect.btn": "Disconnect",
  "mp.sync.done": "Synced",
  "mp.sync.done_body": "Total: {total} (new: {ins}, updated: {upd})",
  "mp.sync.failed": "Sync failed",
  "mp.sync.btn": "Sync products",
  "mp.sync.busy": "Syncing…",

  // settings · delivery url
  "mp.delivery.title": "Delivery link for {mp}",
  "mp.delivery.help_1": "Paste this address into your product on {mp}: ",
  "mp.delivery.help_field": "Unique code → Notification URL",
  "mp.delivery.help_2": ". {mp} adds ",
  "mp.delivery.help_3":
    " on its own and sends the buyer here — we check the code and show them their items.",

  // products list
  "mp.products.loading": "Loading products…",
  "mp.products.load_failed": "Couldn't load the products.",
  "mp.products.empty_1": "No products yet. Hit ",
  "mp.products.empty_2": " to pull your catalog from the marketplace.",
  "mp.products.heading": "Products ({n})",
  "mp.products.heading_hidden": "of {total}; {hidden} hidden",
  "mp.products.heading_hint":
    " — tap a product to open its page with every variant and tool",
  "mp.products.only_enabled": "Only the ones live on {mp}",
  "mp.products.none_filtered":
    "Nothing matches “only live on {mp}” (you have {total} in total). ",
  "mp.products.show_all": "Show all",
  "mp.col.external_id": "External ID",
  "mp.col.title": "Title",
  "mp.col.sales": "Sales",
  "mp.col.status": "Status",
  "mp.col.last_synced": "Last synced",

  // order max-age dropdown
  "mp.maxage.1h": "1 hour",
  "mp.maxage.6h": "6 hours",
  "mp.maxage.24h": "24 hours",
  "mp.maxage.7d": "7 days",

  // branding
  "mp.branding.title": "How buyers see you on {mp}",
  "mp.branding.subtitle":
    "Shown to the buyer on the delivery page. Set separately for each marketplace.",
  "mp.branding.shop_name": "Shop name",
  "mp.branding.shop_ph": "e.g. MyShop",
  "mp.branding.buttons_label": "Buyer buttons",
  "mp.branding.ggsel_note":
    "On the GGsel delivery page the buyer sees “Contact support” and “Leave a review” buttons — both go to the GGsel order page (payment.ggsel.com/order/…). A custom link isn't needed here.",
  "mp.branding.help_label": "“Help” link",
  "mp.branding.help_ph": "https://t.me/yoursupport",
  "mp.branding.maxage_label": "How long after payment auto-delivery stays allowed",
  "mp.branding.maxage_hint":
    "If the buyer opens the delivery page later than this after paying on the marketplace, auto-delivery WON'T run (protection against reusing an old link). The order lands in “Not delivered: old order” — from there you can deliver it by hand or cancel it.",
  "mp.branding.help_must_url": "The link must start with http:// or https://",
  "mp.branding.saved": "Saved",

  // notify events
  "mp.notify.ev.delivery_error.label": "Auto-delivery failed",
  "mp.notify.ev.delivery_error.hint":
    "A sale came in but wasn't delivered automatically — needs your attention.",
  "mp.notify.ev.delivery_ok.label": "Successful delivery",
  "mp.notify.ev.delivery_ok.hint": "Every sale that was delivered successfully.",
  "mp.notify.ev.oos.label": "Out of stock / auto-hide",
  "mp.notify.ev.oos.hint":
    "A variant ran out and was hidden (or came back on sale).",
  "mp.notify.ev.balance.label": "Balance / reconnect",
  "mp.notify.ev.balance.hint":
    "Not enough balance for a sale, or the marketplace token expired.",
  "mp.notify.ev.proxy.label": "Proxy is down",
  "mp.notify.ev.proxy.hint":
    "The account's proxy stopped responding — the account is auto-paused until it's fixed.",

  // notify form
  "mp.notify.title": "Telegram notifications for {mp}",
  "mp.notify.linked": "connected",
  "mp.notify.not_linked": "not connected",
  "mp.notify.subtitle":
    "The bot messages you about this account's events. Connected separately for each marketplace.",
  "mp.notify.connect": "Connect Telegram",
  "mp.notify.open_tg": "Open Telegram and press Start",
  "mp.notify.open_tg_body": "Bot @{bot}. The link is valid for about 15 minutes.",
  "mp.notify.link_failed":
    "Couldn't connect Telegram right now. Please try again in a moment.",
  "mp.notify.flags_saved": "Notification settings saved",
  "mp.notify.unlink_confirm": "Turn off Telegram notifications for this account?",
  "mp.notify.unlinked": "Telegram disconnected",
  "mp.notify.disconnect": "Disconnect",

  // message-template editor
  "mp.tpl.editor_toggle": "Notification text editor",
  "mp.tpl.intro":
    "Tailor the text of each notification. Click a variable to insert it — when the message is sent, variables are replaced with the real order data. An empty template falls back to the default.",
  "mp.tpl.badge_custom": "custom",
  "mp.tpl.badge_unsaved": "unsaved",
  "mp.tpl.preview": "Preview",
  "mp.tpl.reset": "Reset to default",
  "mp.tpl.saved": "Template “{label}” saved",
  "mp.tpl.reset_done": "“{label}” reset to default",
  "mp.tpl.reset_failed": "Couldn't reset",
  "mp.tpl.sample.shop": "My shop",
  "mp.tpl.sample.product": "Game Gift Card 1000₽",
  "mp.tpl.sample.variant": "Region RU",
  "mp.tpl.sample.reason": "the product isn't linked to a service",
  "mp.tpl.sample.oos": "⚠️ Hidden (out of stock): Region RU",

  // topup retry
  "mp.topup.sending": "Sending the top-up for processing…",
  "mp.topup.slow": "It's taking a while — check the order again later.",
  "mp.topup.slow_title": "Still processing",
  "mp.topup.done_title": "Topped up",
  "mp.topup.done": "Done.",
  "mp.topup.fail_title": "Top-up failed",
  "mp.topup.error": "Error.",
  "mp.topup.start_failed": "Couldn't start.",
  "mp.topup.error_title": "Error",
  "mp.topup.retry_label": "Retry the top-up (you can fix the login):",
  "mp.topup.login_ph": "Steam login",
  "mp.topup.retry_btn": "Retry top-up",
  "mp.topup.retry_busy": "Topping up…",

  // stale actions
  "mp.stale.confirm_topup":
    "Deliver by hand? Your balance will be charged at the normal top-up service price.",
  "mp.stale.confirm_codes":
    "Deliver by hand? A code from your inventory and your balance will be used at the normal service price.",
  "mp.stale.releasing": "Starting delivery…",
  "mp.stale.delivered": "Delivered",
  "mp.stale.deliver_failed": "Couldn't deliver",
  "mp.stale.cancel_confirm":
    "Cancel delivery for good? The buyer won't be able to get the item via this link. This can't be undone.",
  "mp.stale.cancelling": "Cancelling…",
  "mp.stale.cancelled": "Order cancelled.",
  "mp.stale.already_cancelled": "It was already cancelled earlier.",
  "mp.stale.cancelled_title": "Cancelled",
  "mp.stale.cancel_failed": "Couldn't cancel.",
  "mp.stale.title": "Paid a while ago — auto-delivery declined",
  "mp.stale.desc":
    "The buyer may have ordered before you connected {mp} to us. You can deliver the item by hand (at your expense, at the normal service price) or cancel the order for good.",
  "mp.stale.login_label": "Steam login (you can fix it):",
  "mp.stale.deliver_btn": "Deliver by hand",
  "mp.stale.cancel_btn": "Cancel for good",
  "mp.stale.slow": "It's taking a while — check the order later.",
  "mp.stale.slow_title": "Still processing",
  "mp.stale.start_failed": "Couldn't start.",
  "mp.common.error_title": "Error",

  // orders section
  "mp.orders.f.product": "Product",
  "mp.orders.f.product_ph": "name from {mp}",
  "mp.orders.f.variant": "Variant",
  "mp.orders.f.variant_pick": "pick a product first",
  "mp.orders.f.variant_none": "no variants",
  "mp.orders.f.variant_any": "any",
  "mp.orders.f.status": "Status",
  "mp.orders.st.all": "all",
  "mp.orders.st.delivered": "Delivered",
  "mp.orders.st.pending": "In progress",
  "mp.orders.st.error": "Delivery error",
  "mp.orders.st.stale": "Not delivered: old order",
  "mp.orders.st.cancelled": "Cancelled",
  "mp.orders.search": "Search",
  "mp.orders.reset": "Reset",
  "mp.orders.f.invoice": "{mp} order №",
  "mp.orders.f.invoice_ph": "e.g. 292149981",
  "mp.orders.f.uniquecode": "Unique code",
  "mp.orders.f.email": "Buyer email",
  "mp.orders.loading": "Loading orders…",
  "mp.orders.none_filter": "Nothing matches your filter.",
  "mp.orders.none": "No orders from {mp} yet.",
  "mp.orders.col.product": "Product ({mp})",
  "mp.orders.col.order_no": "Order №",
  "mp.orders.col.date": "Date",
  "mp.orders.col.buyer": "Buyer",
  "mp.orders.col.variant": "Variant",
  "mp.orders.col.status": "Status",
  "mp.orders.from": "Order from {mp}",
  "mp.orders.code_label": "Code:",
  "mp.order.cancelled_title": "You cancelled this order",
  "mp.order.cancelled_desc":
    "The buyer sees a cancellation message and can't get the item via this link.",
  "mp.order.refunded_title": "Refunded on the marketplace",
  "mp.order.refunded_desc":
    "This order was refunded on the marketplace side (the unique code no longer works). The buyer sees a refund message. If codes were already delivered, they can't be revoked — this is just a note for you.",
  "mp.order.topup_title": "Steam top-up",
  "mp.order.topup_requested": "Requested by the buyer:",
  "mp.order.topup_login_in": "Login on {mp}:",
  "mp.order.topup_changed_seller": "you changed it by hand",
  "mp.order.topup_changed_buyer": "the buyer changed it",
  "mp.order.topup_topped": ", topped up:",
  "mp.order.topup_login": "Login:",
  "mp.order.paid_here": "Paid on our side:",
  "mp.order.codes_count": "Codes delivered:",
  "mp.order.copy_all": "Copy all codes",
  "mp.order.codes_copied": "Codes copied",
  "mp.order.service_fallback": "service #{id}",
  "mp.order.no_codes": "No codes delivered yet.",
  "mp.order.pages": "Page {page} of {pages} · {total} order(s) total",
  "mp.prev": "← Back",
  "mp.next": "Forward →",

  // product row
  "mp.prow.slow_title": "Still processing",
  "mp.prow.slow_body": "The marketplace is still working on it — check after a Sync.",
  "mp.prow.enabled_done": "Product enabled on the marketplace",
  "mp.prow.disabled_done": "Product disabled on the marketplace",
  "mp.prow.status_failed": "Couldn't change the status",
  "mp.prow.status_failed_body": "Nothing changed on our side.",
  "mp.prow.no_title": "(no title)",
  "mp.prow.enabled": "On sale",
  "mp.prow.disabled": "Off",
  "mp.prow.enable": "Enable",
  "mp.prow.disable": "Disable",

  // instruction editor
  "mp.instr.title": "Buyer instructions (this product)",
  "mp.instr.subtitle":
    "Shown to the buyer above the codes on the delivery page (how to activate, etc.). Leave it empty to hide the block.",
  "mp.instr.ph": "e.g. Activate the code in Steam → Add to wallet…",
  "mp.instr.saved": "Instructions saved",
  "mp.instr.save": "Save instructions",

  // auto-hide toggle
  "mp.autohide.title": "Auto-hide when out of stock",
  "mp.autohide.desc":
    "If the linked service runs out, the variant is hidden on the marketplace automatically (if it's the main one, the cheapest available becomes main and prices are recalculated). When every variant is out of stock, the whole product is turned off. When stock returns, everything comes back on its own. Checked roughly every 30 sec.",
  "mp.autohide.on": "Auto-hide on",
  "mp.autohide.off": "Auto-hide off",

  // delivery-kind editor
  "mp.dk.type": "Delivery type:",
  "mp.dk.codes": "Codes",
  "mp.dk.topup": "Steam top-up",
  "mp.dk.type_topup_toast": "Type: Steam top-up",
  "mp.dk.type_codes_toast": "Type: codes",
  "mp.dk.topup_intro":
    "The buyer enters the login in the marketplace form option (set it below). The amount comes from the marketplace order quantity (cnt_goods) in the chosen currency — we convert it to USD (the same rate as the site's Steam top-up page) and top the account up through the chosen service.",
  "mp.dk.currency_label": "Currency of the amount on the marketplace",
  "mp.dk.ccy_usd": "USD (no conversion)",
  "mp.dk.our_service": "Our Steam top-up service (charged to your balance):",
  "mp.dk.service_fallback": "service",
  "mp.dk.not_picked": "not selected",
  "mp.dk.search_ph": "Name, category, menu or #ID…",
  "mp.dk.search_title":
    "Tips: “#3 ” (with a space) — exactly id 3; “#3” (no space) — ids starting with 3; plain text — search by name / category / menu.",
  "mp.dk.login_option": "Marketplace option = Steam login",
  "mp.dk.pick_option": "— pick an option —",
  "mp.dk.no_options":
    "This product has no options on the marketplace — check that the product form is set up (login/amount as parameters).",
  "mp.dk.fill_fields": "Fill the fields in",
  "mp.dk.fill_fields_body": "A service and a login option are required.",
  "mp.dk.topup_saved": "Top-up settings saved",
  "mp.dk.save_topup": "Save top-up settings",

  // substitutions
  "mp.subs.loading": "Loading substitutions…",
  "mp.subs.title": "Denomination substitutions",
  "mp.subs.intro":
    "If a denomination is out of stock, it can be delivered automatically using other denominations from the same product. Example: “1 × $20 ⇒ 2 × $10”. Rules chain: with “$20 ⇒ 2 × $10” and “$10 ⇒ 2 × $5”, a $20 order with only $5 in stock ships as 4 × $5. The out-of-stock check keeps the variant visible as long as at least one chain can cover the order; on delivery we create separate orders for the replacement denominations (at the normal service price). Circular rules (A→B and B→A) won't be saved.",
  "mp.subs.need_mappings":
    "Link variants to services first — there's nothing to substitute without mappings.",
  "mp.subs.none": "No substitution rules yet.",
  "mp.subs.edit": "Edit",
  "mp.subs.if_oos": "If out of stock:",
  "mp.subs.pick_denom": "— pick a denomination —",
  "mp.subs.remove_rule": "Remove rule",
  "mp.subs.replace_with": "replace with (per 1 missing unit):",
  "mp.subs.service": "— service —",
  "mp.subs.qty": "× quantity:",
  "mp.subs.min_one_part": "There must be at least one part",
  "mp.subs.remove": "Remove",
  "mp.subs.add_part": "+ Add part",
  "mp.subs.add_rule": "+ Add rule",
  "mp.subs.save": "Save substitutions",
  "mp.subs.saved": "Substitutions saved",
  "mp.subs.saved_body": "{n} rule(s)",
  "mp.subs.not_saved": "Not saved",
  "mp.subs.save_error": "Couldn't save",
  "mp.subs.v.target": "Pick a target denomination for every rule.",
  "mp.subs.v.dup_rule": "Duplicate rule for the denomination “{name}”.",
  "mp.subs.v.no_parts": "The rule for “{name}” has no replacement parts.",
  "mp.subs.v.part_service": "Pick a service for every replacement part.",
  "mp.subs.v.part_same":
    "A replacement part can't be the same as its own denomination “{name}”.",
  "mp.subs.v.dup_part": "Duplicate part “{p}” in the rule “{name}”.",
  "mp.subs.v.qty": "A part quantity must be 1–1000.",

  // mapping editor
  "mp.map.loading_variants": "Loading variants…",
  "mp.map.variants_failed":
    "Couldn't fetch the product's variants from the marketplace (token / proxy / API response). Try again later.",
  "mp.map.title": "Variant → service links",
  "mp.map.collapse_all": "Collapse all",
  "mp.map.expand_all": "Expand all",
  "mp.map.orphans_title":
    "Links to variants that no longer exist on the marketplace",

  // variant block
  "mp.var.default": "main",
  "mp.var.hidden": "hidden",
  "mp.var.no_links": "no links",
  "mp.var.n_links": "{n} link(s)",
  "mp.var.make_default": "Make it the main one",
  "mp.var.make_default_title":
    "Make this the main variant: its price becomes the product base, the others' add-ons are recalculated, and final buyer prices don't change",
  "mp.var.return_title": "Put the variant back on sale (visible on the marketplace)",
  "mp.var.hide_title":
    "Take off sale: hidden on the marketplace + we stop delivering it",
  "mp.var.return": "Put back on sale",
  "mp.var.hide": "Hide",
  "mp.var.cost_title":
    "What it costs you: exact price with your terms × quantity, summed over all of this variant's links",
  "mp.var.digi_title_src":
    "Variant price on the marketplace: {src} {ccy} ≈ ${usd}; profit = (marketplace − your cost) / your cost",
  "mp.var.digi_title": "Variant price on the marketplace (USD equivalent)",
  "mp.var.key_title": "Variant key — click to select, or press 📋",
  "mp.var.key_copy": "Copy the variant key",
  "mp.var.hidden_done": "Variant hidden on the marketplace",
  "mp.var.return_done": "Variant is back on sale",
  "mp.var.is_default_warn": "This is the default variant.",
  "mp.var.is_default_title": "This is the default variant",
  "mp.var.is_default_body": "Make another variant the main one first.",
  "mp.var.hide_failed_warn":
    "Couldn't hide it on the marketplace: {err}. The variant stays on sale — try again.",
  "mp.var.hide_failed": "Couldn't hide it",
  "mp.var.hide_failed_body": "The variant is still on sale on the marketplace.",
  "mp.var.error": "error",
  "mp.var.failed": "Couldn't do it.",

  // promote / reprice flow
  "mp.promote.preparing": "Preparing the recalculation…",
  "mp.promote.slow_calc": "It's taking a while to calculate. Try again.",
  "mp.promote.calc_failed": "Couldn't calculate.",
  "mp.promote.calc_failed_title": "Couldn't calculate",
  "mp.promote.error": "Error.",
  "mp.promote.applying":
    "The marketplace is recalculating prices… (may take 1–2 minutes)",
  "mp.promote.slow_apply":
    "The marketplace isn't responding for a while. Check the product in your marketplace cabinet and try again.",
  "mp.promote.slow_apply_title": "Slow recalculation",
  "mp.promote.done": "Done",
  "mp.promote.done_hide": "Main changed, the old one hidden, prices saved.",
  "mp.promote.done_nohide": "The variant is now the main one, prices saved.",
  "mp.promote.rejected": "The marketplace rejected it",
  "mp.promote.recalc_error": "Recalculation error.",
  "mp.promote.apply_failed": "Couldn't apply it",
  "mp.promote.title_hide": "Change the main variant so this one can be hidden",
  "mp.promote.title_make": "Make “{label}” the main variant",
  "mp.promote.desc_hide":
    "The marketplace won't let you hide the main variant. Choose which one becomes main — its price becomes the product base, the others' add-ons are recalculated so final buyer prices don't change.",
  "mp.promote.desc_make":
    "This variant's price becomes the product base, the others' add-ons are recalculated — final buyer prices DON'T change. The current main one stays on sale, it just stops being the main.",
  "mp.promote.no_siblings":
    "There are no other variants in this option — nothing to switch the main to.",
  "mp.promote.pick_new": "— pick the new main —",
  "mp.promote.calc": "Calculating…",
  "mp.promote.col.variant": "Variant",
  "mp.promote.col.now": "Now",
  "mp.promote.col.will": "Will be",
  "mp.promote.col.buyer": "Buyer total",
  "mp.promote.will_hidden": "hidden",
  "mp.promote.will_default": "main (base), {mod}",
  "mp.promote.base": "Product base:",
  "mp.promote.base_after": ". Final buyer prices don't change.",
  "mp.promote.applying_btn": "Applying on the marketplace…",
  "mp.promote.apply_btn": "Apply on the marketplace",

  // mapping row
  "mp.maprow.qty_min": "quantity ≥ 1",
  "mp.maprow.saved": "Saved",
  "mp.maprow.cost_title":
    "This link's cost for you (exact, with your terms × quantity)",
  "mp.maprow.qty": "qty",
  "mp.maprow.del_confirm": "Remove the link → {name}?",
  "mp.maprow.del_failed": "Couldn't remove it",

  // add mapping
  "mp.add.pick_service": "Pick a service from the list",
  "mp.add.no_links": "No links yet. Add a service below.",
  "mp.add.hint":
    "#3 ⇒ ids start with 3 · #3 (with a space) ⇒ exactly id 3",
  "mp.add.added": "Link added",
  "mp.add.add_failed": "Couldn't add it",
  "mp.add.btn": "Add",

  // connect form
  "mp.connect.soon": "Coming soon. This marketplace isn't available yet.",
  "mp.connect.fill_all": "Fill in every field",
  "mp.connect.fill_secret": "Fill in the Secret Key",
  "mp.connect.connected": "Account connected",
  "mp.connect.failed": "Couldn't connect",
  "mp.connect.failed_body":
    "Check the seller_id, API key and proxy, then try again.",
  "mp.connect.seller_ph": "seller_id (number)",
  "mp.connect.apikey_ph": "API key",
  "mp.connect.secret_ph": "Secret Key",
  "mp.connect.proxy_ph": "http://user:pass@host:port",
  "mp.connect.proxy_hint":
    "The account talks to the marketplace through this proxy, so a working proxy is required. Format: http://user:pass@host:port or socks5://host:port.",
  "mp.connect.checking": "Checking…",
  "mp.connect.btn": "Connect",

  // pricing panel
  "mp.pricing.anchor_incomplete": "Forced price incomplete",
  "mp.pricing.forced_not_number": "Forced price isn't a number",
  "mp.pricing.markup_not_number": "Markup isn't a number",
  "mp.pricing.pin_incomplete": "Pin incomplete",
  "mp.pricing.pin_need_price": "A variant is picked — set its forced price.",
  "mp.pricing.pin_need_variant": "A price is set — pick the pinned variant.",
  "mp.pricing.settings_saved": "Settings saved",
  "mp.pricing.calc_failed": "Couldn't calculate",
  "mp.pricing.slow": "Slow processing",
  "mp.pricing.slow_body": "The marketplace is slow to apply — check in a minute.",
  "mp.pricing.applied": "Prices applied",
  "mp.pricing.apply_failed": "Couldn't apply it",
  "mp.pricing.title": "Auto-pricing for this product",
  "mp.pricing.desc":
    "You can pin one variant — it gets your forced price in ₽ (it becomes the “from X ₽” dumping anchor). Every other variant is figured by the formula: our USD price × ₽ rate × (1 + markup%). If the anchor is hidden (out of stock), the calc still runs and the other variants aren't broken.",
  "mp.pricing.anchor_label": "Pinned variant (its price = product base)",
  "mp.pricing.anchor_none": "— don't pin —",
  "mp.pricing.main_suffix": "  (main)",
  "mp.pricing.anchor_hint":
    "Usually this is the main variant on the marketplace. If empty, every variant follows the formula.",
  "mp.pricing.forced_label": "Forced price of the pinned variant (₽)",
  "mp.pricing.forced_ph": "e.g. 100",
  "mp.pricing.markup_label": "Default markup (%)",
  "mp.pricing.rules_label": "Per-variant markup rules (optional)",
  "mp.pricing.rule_add": "+ rule",
  "mp.pricing.no_rules": "No rules — the default markup applies everywhere.",
  "mp.pricing.rule_variant_ph": "variant_key (opt.)",
  "mp.pricing.rule_markup_ph": "markup %",
  "mp.pricing.auto_label": "Auto-update roughly every 60 sec",
  "mp.pricing.auto_desc":
    "A background loop watches our price + the ₽ rate and pushes new prices to the marketplace when they change. Turned on separately from the manual button below.",
  "mp.pricing.save": "Save settings",
  "mp.pricing.preview_btn": "Calculate a preview",
  "mp.pricing.apply_now_title": "Apply the calculated prices on the marketplace",
  "mp.pricing.apply_now": "Apply now",
  "mp.pricing.applying": "Applying on the marketplace…",
  "mp.pricing.last_run": "Last run:",
  "mp.pricing.last_error": "Error:",
  "mp.pricing.rate": "Rate:",
  "mp.pricing.col.variant": "Variant",
  "mp.pricing.col.our": "our $",
  "mp.pricing.col.markup": "markup %",
  "mp.pricing.col.price_rub": "price ₽",
  "mp.pricing.col.modifier": "modifier",
  "mp.pricing.anchor_tag": "anchor",
  "mp.pricing.base_inline": " · marketplace base: ",
  "mp.pricing.default_inline": " · default variant:",
  "mp.pricing.anchor_inline": " · anchor: ",
};

const RU: Record<string, string> = {
  // ---- nav / layout ----
  "nav.home": "Главная",
  "nav.explore": "Каталог",
  "nav.steam_gifts": "Steam Подарки",
  "nav.steam_topup": "Пополнение Steam",
  "nav.direct_topup": "Прямое пополнение",
  "nav.orders": "Заказы",
  "nav.transactions": "Транзакции",
  "nav.balance": "Баланс",
  "nav.profile": "Профиль",
  "nav.shop": "Магазин",
  "nav.account": "Аккаунт",
  "nav.marketplaces": "Маркетплейсы",
  "nav.admin": "Админ",
  "nav.api_playground": "API Playground",
  "nav.logout": "Выйти",
  "nav.greeting.morning": "Доброе утро",
  "nav.greeting.afternoon": "Добрый день",
  "nav.greeting.evening": "Добрый вечер",
  "nav.greeting.night": "Доброй ночи",

  // ---- home ----
  "home.subtitle": "Кратко по твоему аккаунту на сегодня.",
  "home.kpi.balance": "Баланс",
  "home.kpi.balance.live": "USD · сейчас",
  "home.kpi.orders": "Всего заказов",
  "home.kpi.orders.alltime": "За всё время",
  "home.kpi.last_topup": "Последнее пополнение",
  "home.kpi.last_topup.none": "Пополнений пока нет",
  "home.kpi.account": "Аккаунт",
  "home.kpi.recent": "Последняя активность",
  "home.recent.orders": "Последние заказы",
  "home.recent.transactions": "Последние транзакции",
  "home.recent.movements": "Движения баланса",
  "home.recent.no_orders": "Заказов пока нет.",
  "home.recent.no_transactions": "Транзакций пока нет.",
  "home.recent.start_exploring": "Открыть каталог →",
  "home.recent.view_all": "Все →",
  "home.greeting.fallback": "друг",
  "home.quick.title": "Быстрые действия",
  "home.quick.topup_steam": "Пополнить Steam",
  "home.quick.buy_gift": "Купить Steam-подарок",
  "home.quick.explore_catalog": "Открыть каталог",
  "home.action.explore": "Открыть каталог",
  "home.action.explore.desc":
    "Все подарочные карты, пополнения и коды в одном поиске.",
  "home.action.steam_gifts": "Steam Подарки",
  "home.action.steam_gifts.desc":
    "Отправь любую игру Steam в подарок другу в любой регион.",
  "home.action.steam_topup": "Пополнение Steam",
  "home.action.steam_topup.desc":
    "Пополни Steam-аккаунт в RUB / KZT / UAH.",
  "home.action.balance": "Пополнить баланс",
  "home.action.balance.desc":
    "Перевод Bybit или депозит TON - мгновенное зачисление.",

  // ---- orders ----
  "orders.title": "Заказы",
  "orders.subtitle": "История покупок. Кликни на строку - увидишь коды и статус.",
  "orders.filter.status": "Статус",
  "orders.filter.custom_id": "Custom ID",
  "orders.filter.account_id": "Account ID",
  "orders.filter.account_id_placeholder": "Логин получателя в Steam...",
  "orders.filter.custom_id_placeholder": "UUID...",
  "orders.col.date": "Дата",
  "orders.col.product": "Товар",
  "orders.col.qty": "Кол-во",
  "orders.col.total": "Сумма",
  "orders.col.status": "Статус",
  "orders.col.custom_id": "Custom ID",
  "orders.empty": "Заказов нет.",
  "orders.loading": "Загрузка...",
  "orders.steam_topup_label": "Пополнение Steam",
  "orders.status.all": "Все",
  "orders.status.new": "Новый",
  "orders.status.in_progress": "В процессе",
  "orders.status.completed": "Выполнен",
  "orders.status.refunded": "Возврат",
  "orders.status.canceled": "Отменён",
  "orders.page": "Страница",
  "orders.page.of": "из",
  "orders.page.total": "всего",
  "orders.page.prev": "← Назад",
  "orders.page.next": "Вперёд →",

  // ---- order detail ----
  "order.title": "Заказ",
  "order.field.status": "Статус",
  "order.field.date": "Дата",
  "order.field.product": "Товар",
  "order.field.quantity": "Количество",
  "order.field.total": "Сумма",
  "order.field.codes": "Выданные коды",
  "order.field.payload": "Данные",
  "order.copy": "Скопировать",
  "order.copy_all": "Скопировать всё",
  "order.copied": "Скопировано ✓",
  "order.copy_failed": "Не удалось скопировать",
  "order.copy_all_failed": "Не удалось скопировать всё",
  "order.download_txt": "Скачать .txt",
  "order.back": "← К заказам",
  "order.not_found": "Заказ не найден.",

  // ---- transactions ----
  "tx.title": "Транзакции",
  "tx.subtitle": "Все движения по балансу твоего аккаунта.",
  "tx.filter.type": "Тип",
  "tx.filter.custom_id": "Custom ID",
  "tx.filter.from": "С",
  "tx.filter.to": "По",
  "tx.filter.custom_id_placeholder": "UUID...",
  "tx.col.date": "Дата",
  "tx.col.type": "Тип",
  "tx.col.custom_id": "Custom ID",
  "tx.col.amount": "Сумма",
  "tx.col.balance": "Баланс",
  "tx.col.balance_after": "Баланс после",
  "tx.col.reference": "Описание",
  "tx.empty": "Под фильтр ничего не подошло.",
  "tx.type.all": "Все",
  "tx.type.topup": "Пополнение",
  "tx.type.purchase": "Покупка",
  "tx.type.refund": "Возврат",
  "tx.type.deposit": "Депозит",
  "tx.type.withdraw": "Вывод",
  "tx.type.admin_topup": "Ручное начисление",
  "tx.type.admin_debit": "Ручное списание",
  "tx.col.details": "Подробнее",
  "tx.detail.balance_before": "Баланс до",
  "tx.detail.balance_after": "Баланс после",
  "tx.detail.reason": "Причина",
  "tx.export": "Выгрузить",
  "tx.export.csv": "CSV",
  "tx.export.xlsx": "Excel",
  "tx.export.busy": "Выгружаю...",
  "tx.export.error": "Не удалось выгрузить. Попробуйте ещё раз.",

  // ---- balance ----
  "balance.title": "Баланс",
  "balance.subtitle": "Пополни через Bybit Internal Transfer или сеть TON.",
  "balance.available": "Доступно",
  "balance.live": "обновляется каждые 15 с",
  "balance.tab.bybit": "Bybit",
  "balance.tab.bybit.sub": "Внутренний перевод",
  "balance.tab.ton": "TON",
  "balance.tab.ton.sub": "Только USDT",
  "balance.bybit.title": "Внутренний перевод Bybit",
  "balance.bybit.intro":
    "Только USDT. Отправляй через функцию Internal Transfer в Bybit - без комиссии между Bybit-аккаунтами, зачисление за несколько минут.",
  "balance.bybit.step1": "Сохрани UID отправителя",
  "balance.bybit.step1.desc":
    "UID твоего Bybit-аккаунта. Пополнения зачисляются на баланс только при переводе с этого конкретного UID.",
  "balance.bybit.step2": "Отправь на наш Bybit UID",
  "balance.bybit.copy_label": "UID оператора",
  "balance.bybit.copy_note":
    "Bybit → Assets → Internal Transfer. Отправляй USDT с того же аккаунта, чей UID указал в шаге 1.",
  "balance.bybit.no_uid_notice":
    "Сначала сохрани свой UID в шаге 1 - пока этого не сделано, мы скрываем UID оператора, и пополнение всё равно не зачислится автоматически.",
  "balance.bybit.uid_label": "Bybit UID",
  "balance.bybit.uid_placeholder": "например 12345678",
  "balance.bybit.uid_required": "Нужно ввести UID",
  "balance.bybit.set_uid": "Указать UID",
  "balance.bybit.change": "Изменить",
  "balance.bybit.not_set": "не указан",
  "balance.bybit.uid_saved": "UID отправителя сохранён",
  "balance.bybit.confirm.title": "Подтверди смену UID",
  "balance.bybit.confirm.desc":
    "Это поле определяет, кому зачислится пополнение - подтверди кодом из приложения.",
  "balance.bybit.save_failed": "Не удалось сохранить",
  "balance.bybit.save_failed.body": "Возможно, неверный пароль.",
  "balance.password.current": "Текущий пароль",
  "balance.wrong_code": "Неверный код - попробуй ещё раз.",
  "balance.ton.title": "Сеть TON",
  "balance.ton.unconfigured":
    "TON-депозиты сейчас недоступны - адрес кошелька оператора не настроен. Используй Bybit или напиши в поддержку.",
  "balance.ton.intro":
    "Для тех, у кого нет Bybit. Отправляй USDT в сети TON - любой другой токен не зачислится.",
  "balance.ton.warning":
    "Отправляй только USDT в сети TON. Нативный TON, NOT, jUSDT из других сетей или другие jetton'ы будут потеряны - система ловит только переводы USDT-jetton.",
  "balance.ton.step1": "Сгенерируй TON memo",
  "balance.ton.step1.desc":
    "Короткая случайная метка - по ней система понимает, чей это депозит. Memo виден публично в блокчейне, поэтому он специально обезличен - никаких логинов, email и прочего.",
  "balance.ton.step2": "Отправь USDT на наш TON-адрес",
  "balance.ton.copy_label": "Адрес кошелька",
  "balance.ton.copy_note":
    "Используй TON-кошелёк с поддержкой jetton'ов (Tonkeeper, MyTonWallet, Bybit Web3 и т.д.). Выбери актив USDT, не TON. Memo вставь в поле comment / memo при переводе.",
  "balance.ton.no_memo_notice":
    "Сначала сгенерируй memo в шаге 1 - без него депозит не зачислится автоматически.",
  "balance.ton.not_generated": "не сгенерирован",
  "balance.ton.copy": "Скопировать",
  "balance.ton.copied": "Скопировано ✓",
  "balance.ton.generate_new": "Сгенерировать новый",
  "balance.ton.generate": "Сгенерировать",
  "balance.ton.rotate_btn": "Сменить memo",
  "balance.ton.generate_btn": "Сгенерировать memo",
  "balance.ton.generating": "Генерируем...",
  "balance.ton.confirm.rotate.title": "Подтверди смену memo",
  "balance.ton.confirm.rotate.desc":
    "Смена memo делает любой ожидающий TON-перевод с старым memo неатрибутируемым - подтверди кодом из приложения.",
  "balance.ton.confirm.create.title": "Подтверди создание memo",
  "balance.ton.confirm.create.desc":
    "Генерируем TON memo - подтверди кодом из приложения.",
  "balance.ton.rotated": "TON memo сменён",
  "balance.ton.generated": "TON memo создан",
  "balance.ton.rotate_warning":
    "Смена заменит текущий memo. Любой незавершённый TON-перевод со старым memo придёт без атрибуции и потребует ручного разбора.",
  "balance.ton.could_not_generate": "Не удалось сгенерировать (неверный пароль?)",
  "balance.ton.generation_failed": "Ошибка генерации",
  "balance.copy_failed": "Не удалось скопировать",
  "balance.saving": "Сохраняем...",

  // ---- steam topup ----
  "steam_topup.title": "Пополнение Steam",
  "steam_topup.subtitle":
    "Прямое пополнение кошелька Steam. Введи логин и сколько USD отправить.",
  "steam_topup.login_label": "Логин Steam или email",
  "steam_topup.login_placeholder": "your_steam_login",
  "steam_topup.amount_label": "Сумма, USD",
  "steam_topup.amount_placeholder": "например 5.00",
  "steam_topup.cta": "Пополнить",
  "steam_topup.cta_busy": "Отправляем...",
  "steam_topup.invalid_login": "Неверный логин Steam",
  "steam_topup.invalid_login_body":
    "Введи имя аккаунта Steam или привязанный к нему email.",
  "steam_topup.invalid_amount": "Неверная сумма",
  "steam_topup.completed": "Пополнение выполнено",
  "steam_topup.in_progress": "Пополнение в процессе",
  "steam_topup.refunded": "Пополнение возвращено",
  "steam_topup.insufficient": "Недостаточно средств",
  "steam_topup.view_order": "Открыть заказ",

  // ---- direct top-up ----
  "direct_topup.title": "Прямое пополнение",
  "direct_topup.subtitle":
    "Пополни нужный сервис напрямую - выбери продукт, введи свой ID и сумму.",
  "direct_topup.empty":
    "Сейчас нет доступных пополнений. Загляни позже.",
  "direct_topup.search": "Поиск по названию игры",
  "direct_topup.choose_amount": "Выбери пакет",
  "direct_topup.buy": "Купить",
  "direct_topup.buying": "Покупаем...",
  "direct_topup.completed": "Пополнение выполнено",
  "direct_topup.in_progress": "Пополнение в процессе",
  "direct_topup.refunded": "Пополнение возвращено",
  "direct_topup.insufficient": "Недостаточно средств",
  "direct_topup.no_fields": "Нет полей для заполнения.",
  "direct_topup.required": "Обязательно",
  "direct_topup.invalid_value": "Неверное значение",
  "direct_topup.back": "Ко всем пополнениям",

  // ---- steam gifts ----
  "steam_gift.title": "Steam Подарки",
  "steam_gift.subtitle":
    "Найди игру Steam по названию или App ID и отправь её в подарок по ссылке-приглашению друга.",
  "steam_gift.unconfigured":
    "Сервис Steam Gift не настроен для этого аккаунта. Обратись к менеджеру.",
  "steam_gift.search.placeholder":
    "Поиск по названию или App ID (например 730, 'Counter-Strike')...",
  "steam_gift.search.start": "Начни вводить, чтобы искать по каталогу Steam.",
  "steam_gift.search.busy": "Ищем...",
  "steam_gift.search.failed": "Ошибка поиска.",
  "steam_gift.search.empty": "Под запрос «{q}» ничего не нашлось.",
  "steam_gift.no_image": "нет картинки",
  "steam_gift.app": "App",
  "steam_gift.detail.loading": "Загрузка...",
  "steam_gift.detail.no_subs": "У этой игры нет доступных пакетов для покупки.",
  "steam_gift.region_label": "Регион",
  "steam_gift.app_label": "Игра",
  "steam_gift.friend_label": "Ссылка-приглашение Steam",
  "steam_gift.friend_placeholder": "https://s.team/p/abc-defg/h12345",
  "steam_gift.gift_title_label": "Название подарка",
  "steam_gift.gift_description_label": "Описание",
  "steam_gift.gift_description_placeholder": "необязательно",
  "steam_gift.youll_pay": "К оплате",
  "steam_gift.pick_region": "Выбери регион в таблице.",
  "steam_gift.cta": "Купить",
  "steam_gift.cta_busy": "Отправляем...",

  // ---- catalog / category ----
  "catalog.title": "Каталог",
  "catalog.col.service": "Товар",
  "catalog.col.price": "Цена",
  "catalog.col.in_stock": "В наличии",
  "catalog.col.qty": "Кол-во",
  "catalog.col.action": "Действие",
  "catalog.buy": "Купить",
  "catalog.buy_all": "Купить всё",
  "catalog.buy_busy": "Покупаем...",
  "catalog.in_stock": "в наличии",
  "catalog.out_of_stock": "нет в наличии",
  "catalog.out": "нет",
  "catalog.no_services": "В этой категории нет товаров.",
  "catalog.bulk.set_qty": "Укажи количество и купи по отдельности или сразу всё.",
  "catalog.bulk.selected": "выбрано",
  "catalog.bulk.total": "всего",
  "catalog.unsupported_fields":
    "Эта категория требует дополнительных полей помимо количества. Покупка прямо здесь пока не поддерживается - обратись к менеджеру.",
  "catalog.back": "← Каталог",

  // ---- explore ----
  "menu.back": "← Каталог",
  "menu.not_found": "Раздел не найден.",
  "menu.empty": "Пока нет категорий.",
  "explore.title": "Каталог",
  "explore.subtitle.search":
    "Категории по запросу (совпадение по имени категории или товара).",
  "explore.subtitle.browse":
    "Выбери раздел или начни вводить для поиска.",
  "explore.search.placeholder": "Поиск по категориям и товарам...",
  "explore.empty": "Под запрос ничего не нашлось.",
  "explore.failed": "Не удалось загрузить каталог.",
  "explore.menus.one": "раздел",
  "explore.menus.many": "разделов",
  "explore.categories.one": "категория",
  "explore.categories.many": "категорий",
  "explore.services.one": "товар",
  "explore.services.many": "товаров",
  "explore.match": "совпадение",

  // ---- profile ----
  "profile.title": "Профиль",
  "profile.subtitle": "Аккаунт, безопасность и активные сессии.",
  "profile.field.login": "Логин",
  "profile.field.display_name": "Имя",
  "profile.field.balance": "Баланс",
  "profile.field.rights": "Права",
  "profile.field.rights.none": "нет",
  "profile.section.account": "Аккаунт",
  "profile.section.password": "Сменить пароль",
  "profile.section.password.subtitle":
    "Смена пароля разлогинит все устройства, включая это.",
  "profile.section.totp": "Двухфакторная аутентификация",
  "profile.section.sessions": "Активные сессии",
  "profile.section.sessions.subtitle":
    "Сессии браузера, привязанные к твоему аккаунту. Кнопкой справа можно выйти везде, включая это устройство.",
  "profile.password.current": "Текущий пароль",
  "profile.password.new": "Новый пароль",
  "profile.password.confirm": "Повтори новый пароль",
  "profile.password.cta": "Сменить пароль",
  "profile.password.busy": "Меняем...",
  "profile.password.mismatch": "Пароли не совпадают",
  "profile.password.changed": "Пароль сменён",
  "profile.password.changed.body":
    "Все сессии разлогинены. Войди заново.",
  "profile.password.fail": "Не удалось сменить пароль",
  "profile.totp.enabled": "Включена",
  "profile.totp.disabled": "Отключена",
  "profile.totp.enable": "Включить",
  "profile.totp.disable": "Отключить",
  "profile.totp.subtitle":
    "Подойдёт любое приложение TOTP (Google Authenticator, Authy, 1Password, Aegis...). Включи отдельно проверку при входе и при каждой покупке - при массовой покупке код спрашивается один раз на пачку.",
  "profile.totp.password_hint":
    "Это нужно чтобы по украденной cookie-сессии нельзя было привязать 2FA к чужому приложению.",
  "profile.totp.generating": "Генерируем...",
  "profile.totp.enable_btn": "Включить 2FA",
  "profile.totp.step1.title": "Шаг 1 - добавь в приложение",
  "profile.totp.step1.desc":
    "Отсканируй QR в приложении или скопируй секрет вручную.",
  "profile.totp.secret_label": "Секрет (вручную)",
  "profile.totp.qr_alt": "QR для приложения",
  "profile.totp.issuer_note":
    "Issuer: NS Cabinet. Алгоритм SHA1, 6 цифр, период 30 с.",
  "profile.totp.step2.title": "Шаг 2 - подтверди код",
  "profile.totp.confirming": "Подтверждаем...",
  "profile.totp.activate_btn": "Активировать",
  "profile.totp.setup_failed": "Не удалось настроить",
  "profile.totp.setup_failed.body":
    "Неверный пароль или 2FA уже в процессе настройки.",
  "profile.totp.activated": "2FA включена",
  "profile.totp.activated.body":
    "Запрос кода активен и при входе, и при покупках. Можно поменять ниже.",
  "profile.totp.wrong_code": "Неверный код",
  "profile.totp.wrong_code.body": "Попробуй следующий код из приложения.",
  "profile.totp.disabled_toast": "2FA отключена",
  "profile.totp.cannot_disable": "Не удалось отключить.",
  "profile.totp.confirm_with_app": "Подтверди кодом из приложения.",
  "profile.totp.wrong_code.retry": "Неверный код, попробуй ещё раз.",
  "profile.totp.save_failed": "Не удалось сохранить настройку",
  "profile.totp.active": "2FA активна.",
  "profile.totp.toggle.login": "Спрашивать код при входе",
  "profile.totp.toggle.login.hint": "Один раз за сессию входа.",
  "profile.totp.toggle.purchase": "Спрашивать код при покупке",
  "profile.totp.toggle.purchase.hint":
    "Один раз на пачку (при массовой покупке тот же код используется для всех заказов).",
  "profile.totp.toggle.deposit": "Спрашивать код при смене Bybit UID",
  "profile.totp.toggle.deposit.hint":
    "При добавлении, изменении или удалении UID отправителя Bybit.",
  "profile.totp.action.enable_login": "Включить 2FA при входе",
  "profile.totp.action.disable_login": "Отключить 2FA при входе",
  "profile.totp.action.enable_purchase": "Включить 2FA при покупке",
  "profile.totp.action.disable_purchase": "Отключить 2FA при покупке",
  "profile.totp.action.enable_deposit": "Включить 2FA при смене UID",
  "profile.totp.action.disable_deposit": "Отключить 2FA при смене UID",
  "profile.totp.disable_label": "Отключить 2FA",
  "profile.totp.disabling": "Отключаем...",
  "profile.totp.disable_btn": "Отключить 2FA",
  "profile.sessions.this_device": "это устройство",
  "profile.sessions.started": "начата",
  "profile.sessions.last_seen": "активность",
  "profile.sessions.empty": "Активных сессий нет.",
  "profile.sessions.revoke_all": "Выйти везде",
  "profile.sessions.revoke_all.busy": "Выходим...",
  "profile.sessions.revoked.title": "Вышли везде",
  "profile.sessions.revoked.body": "Сессий отозвано: {n}.",
  "profile.sessions.revoke_failed": "Не удалось выйти",
  "profile.logout": "Выйти",

  // ---- login ----
  "login.title": "Вход",
  "login.login_label": "Логин",
  "login.password_label": "Пароль",
  "login.cta": "Войти",
  "login.cta_busy": "Входим...",
  "login.error.invalid": "Неверный логин или пароль",
  "login.totp_required": "Нужен код 2FA",
  "login.totp_label": "Код 2FA",
  "login.signup_hint": "Нет аккаунта?",
  "login.signup_cta": "Зарегистрироваться",
  "login.back_to_login": "← К входу",

  // ---- common ----
  "common.loading": "Загрузка...",
  "common.error": "Ошибка",
  "common.retry": "Повторить",
  "common.copy": "Скопировать",
  "common.copied": "Скопировано",
  "common.cancel": "Отмена",
  "common.save": "Сохранить",
  "common.close": "Закрыть",
  "common.confirm": "Подтвердить",
  "common.dash": "-",

  // ---- marketplaces ----
  "mp.loading": "Загрузка…",
  "mp.back": "← К площадкам",
  "mp.save": "Сохранить",
  "mp.cancel": "Отмена",
  "mp.delete": "Удалить",
  "mp.copy": "Копировать",
  "mp.copied": "Скопировано",
  "mp.copy_failed": "Не удалось скопировать",
  "mp.toast.not_saved": "Не удалось сохранить",

  // hub
  "mp.hub.title": "Маркетплейсы",
  "mp.hub.subtitle": "Выберите площадку, с которой будете работать.",
  "mp.badge.soon": "Скоро",
  "mp.badge.connected": "Подключён",
  "mp.badge.disconnected": "Не подключён",
  "mp.hub.card.soon": "Пока недоступно",
  "mp.hub.card.tabs": "Товары · Заказы · Настройки",

  // product page
  "mp.product.invalid": "Такого товара нет.",
  "mp.product.title_fallback": "Товар",
  "mp.product.intro":
    "Здесь всё по этому товару: привязка вариантов к вашим услугам, инструкция для покупателя, скрытие варианта и смена основного.",

  // integration page
  "mp.integration.unknown": "Неизвестная площадка.",
  "mp.integration.soon": "{mp} — скоро. Пока недоступно.",
  "mp.integration.connect_intro":
    "Аккаунт ещё не подключён. Подключите своего продавца, чтобы появились товары, заказы и настройки. Для каждого аккаунта нужен отдельный прокси — через него идёт всё общение с площадкой.",
  "mp.tab.products": "Товары",
  "mp.tab.orders": "Заказы",
  "mp.tab.settings": "Настройки",

  // proxy form
  "mp.proxy.title": "Прокси аккаунта",
  "mp.proxy.current": "Текущий:",
  "mp.proxy.direct": "напрямую (без прокси)",
  "mp.proxy.help":
    "Новый прокси мы проверяем входом в {mp} перед сохранением — если он не работает, он не сохранится. Рабочий прокси заодно снимает авто-паузу.",
  "mp.proxy.placeholder": "http://user:pass@host:port  или  socks5://host:port",
  "mp.proxy.save": "Сохранить прокси",
  "mp.proxy.checking": "Проверяем…",
  "mp.proxy.saved": "Прокси обновлён и проверен",
  "mp.proxy.not_saved": "Прокси не сохранён",
  "mp.proxy.check_failed": "Новый прокси не прошёл проверку.",

  // api-key rotation form
  "mp.apikey.title": "API-ключ",
  "mp.apikey.help":
    "Смените API-ключ {mp} без отвязки магазина — привязки товаров сохранятся. Новый ключ мы проверяем входом в {mp} перед сохранением; нерабочий ключ не сохранится.",
  "mp.apikey.placeholder": "Новый API-ключ",
  "mp.apikey.save": "Сохранить ключ",
  "mp.apikey.checking": "Проверяем…",
  "mp.apikey.saved": "API-ключ обновлён и проверен",
  "mp.apikey.not_saved": "API-ключ не сохранён",
  "mp.apikey.check_failed": "Новый API-ключ не прошёл проверку.",

  // settings · proxy down
  "mp.proxydown.title": "Аккаунт на авто-паузе: прокси недоступен",
  "mp.proxydown.desc":
    "Запросы к {mp} приостановлены — мы не стучимся через мёртвый прокси и не ходим с нашего IP. Почините прокси и нажмите кнопку: мы перепроверим его и снова включим аккаунт.",
  "mp.proxydown.recheck": "Перепроверить прокси и включить",
  "mp.proxydown.busy": "Проверяем…",
  "mp.proxydown.resumed": "Аккаунт снова активен (прокси работает)",
  "mp.proxydown.still_down": "Прокси всё ещё недоступен",

  // settings · fields + actions
  "mp.field.status": "Статус",
  "mp.field.proxy": "Прокси",
  "mp.field.connected": "Подключён",
  "mp.field.last_sync": "Последняя синхронизация",
  "mp.field.never": "никогда",
  "mp.field.direct": "напрямую",
  "mp.disconnect.confirm":
    "Отключить аккаунт {mp} (seller_id={sid})? Привязанные товары и привязки тоже удалятся.",
  "mp.disconnect.done": "Аккаунт отключён",
  "mp.disconnect.failed": "Не удалось отключить",
  "mp.disconnect.btn": "Отключить",
  "mp.sync.done": "Готово",
  "mp.sync.done_body": "Всего: {total} (новых: {ins}, обновлено: {upd})",
  "mp.sync.failed": "Синхронизация не удалась",
  "mp.sync.btn": "Обновить товары",
  "mp.sync.busy": "Обновляем…",

  // settings · delivery url
  "mp.delivery.title": "Ссылка выдачи для {mp}",
  "mp.delivery.help_1": "Вставьте этот адрес в настройках товара на {mp}: ",
  "mp.delivery.help_field": "Уникальный код → URL уведомления",
  "mp.delivery.help_2": ". {mp} сам добавит ",
  "mp.delivery.help_3":
    " и отправит покупателя сюда — мы проверим код и покажем ему его товары.",

  // products list
  "mp.products.loading": "Загружаем товары…",
  "mp.products.load_failed": "Не удалось загрузить товары.",
  "mp.products.empty_1": "Товаров пока нет. Нажмите ",
  "mp.products.empty_2": ", чтобы подтянуть каталог с площадки.",
  "mp.products.heading": "Товары ({n})",
  "mp.products.heading_hidden": "из {total}; {hidden} скрыто",
  "mp.products.heading_hint":
    " — нажмите на товар, чтобы открыть его страницу со всеми вариантами и инструментами",
  "mp.products.only_enabled": "Только то, что продаётся на {mp}",
  "mp.products.none_filtered":
    "По фильтру «только то, что продаётся на {mp}» ничего нет (всего {total}). ",
  "mp.products.show_all": "Показать все",
  "mp.col.external_id": "Внешний ID",
  "mp.col.title": "Название",
  "mp.col.sales": "Продажи",
  "mp.col.status": "Статус",
  "mp.col.last_synced": "Обновлено",

  // order max-age dropdown
  "mp.maxage.1h": "1 час",
  "mp.maxage.6h": "6 часов",
  "mp.maxage.24h": "24 часа",
  "mp.maxage.7d": "7 дней",

  // branding
  "mp.branding.title": "Как вас видит покупатель на {mp}",
  "mp.branding.subtitle":
    "Показывается покупателю на странице выдачи. Своё для каждой площадки.",
  "mp.branding.shop_name": "Название магазина",
  "mp.branding.shop_ph": "напр. MyShop",
  "mp.branding.buttons_label": "Кнопки покупателю",
  "mp.branding.ggsel_note":
    "На странице выдачи GGsel покупателю показываются кнопки «Связаться с поддержкой» и «Оставить отзыв» — обе ведут на страницу заказа GGsel (payment.ggsel.com/order/…). Своя ссылка здесь не нужна.",
  "mp.branding.help_label": "Ссылка «Помощь»",
  "mp.branding.help_ph": "https://t.me/yoursupport",
  "mp.branding.maxage_label": "Сколько после оплаты разрешена авто-выдача",
  "mp.branding.maxage_hint":
    "Если покупатель открыл страницу выдачи позже этого срока после оплаты на площадке — авто-выдача НЕ сработает (защита от повторного использования старой ссылки). Заказ попадёт в «Не выдан: старый заказ» — оттуда можно выдать вручную или отменить.",
  "mp.branding.help_must_url": "Ссылка должна начинаться с http:// или https://",
  "mp.branding.saved": "Сохранено",

  // notify events
  "mp.notify.ev.delivery_error.label": "Авто-выдача не сработала",
  "mp.notify.ev.delivery_error.hint":
    "Продажа пришла, но не выдалась автоматически — нужно вмешаться.",
  "mp.notify.ev.delivery_ok.label": "Успешная выдача",
  "mp.notify.ev.delivery_ok.hint": "Каждая успешно выданная продажа.",
  "mp.notify.ev.oos.label": "Нет в наличии / авто-скрытие",
  "mp.notify.ev.oos.hint":
    "Вариант кончился и был скрыт (или вернулся в продажу).",
  "mp.notify.ev.balance.label": "Баланс / переподключение",
  "mp.notify.ev.balance.hint":
    "Не хватило баланса на продажу, или истёк токен площадки.",
  "mp.notify.ev.proxy.label": "Прокси не работает",
  "mp.notify.ev.proxy.hint":
    "Прокси аккаунта перестал отвечать — аккаунт на авто-паузе до починки.",

  // notify form
  "mp.notify.title": "Уведомления в Telegram для {mp}",
  "mp.notify.linked": "подключено",
  "mp.notify.not_linked": "не подключено",
  "mp.notify.subtitle":
    "Бот будет писать вам о событиях этого аккаунта. Подключается отдельно для каждой площадки.",
  "mp.notify.connect": "Подключить Telegram",
  "mp.notify.open_tg": "Откройте Telegram и нажмите Start",
  "mp.notify.open_tg_body": "Бот @{bot}. Ссылка действует около 15 минут.",
  "mp.notify.link_failed":
    "Сейчас не получилось подключить Telegram. Попробуйте ещё раз чуть позже.",
  "mp.notify.flags_saved": "Настройки уведомлений сохранены",
  "mp.notify.unlink_confirm": "Отключить Telegram-уведомления для этого аккаунта?",
  "mp.notify.unlinked": "Telegram отключён",
  "mp.notify.disconnect": "Отключить",

  // message-template editor
  "mp.tpl.editor_toggle": "Редактор текста уведомлений",
  "mp.tpl.intro":
    "Настройте текст каждого уведомления. Нажмите на переменную, чтобы вставить её — при отправке она заменится на реальные данные заказа. Пустой шаблон вернёт текст по умолчанию.",
  "mp.tpl.badge_custom": "своё",
  "mp.tpl.badge_unsaved": "не сохранено",
  "mp.tpl.preview": "Предпросмотр",
  "mp.tpl.reset": "Вернуть стандартный",
  "mp.tpl.saved": "Шаблон «{label}» сохранён",
  "mp.tpl.reset_done": "«{label}» возвращён к стандартному",
  "mp.tpl.reset_failed": "Не удалось сбросить",
  "mp.tpl.sample.shop": "Мой магазин",
  "mp.tpl.sample.product": "Game Gift Card 1000₽",
  "mp.tpl.sample.variant": "Регион RU",
  "mp.tpl.sample.reason": "товар не привязан к услуге",
  "mp.tpl.sample.oos": "⚠️ Скрыто (нет в наличии): Регион RU",

  // topup retry
  "mp.topup.sending": "Отправляем пополнение в обработку…",
  "mp.topup.slow": "Долго обрабатывается — проверьте заказ чуть позже.",
  "mp.topup.slow_title": "Долгая обработка",
  "mp.topup.done_title": "Пополнено",
  "mp.topup.done": "Готово.",
  "mp.topup.fail_title": "Не удалось пополнить",
  "mp.topup.error": "Ошибка.",
  "mp.topup.start_failed": "Не удалось запустить.",
  "mp.topup.error_title": "Ошибка",
  "mp.topup.retry_label": "Повторить пополнение (можно исправить логин):",
  "mp.topup.login_ph": "логин Steam",
  "mp.topup.retry_btn": "Повторить пополнение",
  "mp.topup.retry_busy": "Пополняем…",

  // stale actions
  "mp.stale.confirm_topup":
    "Выдать вручную? Спишется с вашего баланса по обычной цене сервиса пополнения.",
  "mp.stale.confirm_codes":
    "Выдать вручную? Спишется код из вашего инвентаря и баланс по обычной цене сервиса.",
  "mp.stale.releasing": "Запускаем выдачу…",
  "mp.stale.delivered": "Выдано",
  "mp.stale.deliver_failed": "Не удалось выдать",
  "mp.stale.cancel_confirm":
    "Отменить выдачу навсегда? Покупатель больше не сможет получить товар по этой ссылке. Действие необратимо.",
  "mp.stale.cancelling": "Отменяем…",
  "mp.stale.cancelled": "Заказ отменён.",
  "mp.stale.already_cancelled": "Уже было отменено ранее.",
  "mp.stale.cancelled_title": "Отменено",
  "mp.stale.cancel_failed": "Не удалось отменить.",
  "mp.stale.title": "Оплачен давно — авто-выдача отклонена",
  "mp.stale.desc":
    "Возможно, покупатель оформил заказ до того, как вы подключили {mp} к нам. Можно выдать товар вручную (за ваш счёт по обычной цене сервиса) или отменить заказ навсегда.",
  "mp.stale.login_label": "Логин Steam (можно исправить):",
  "mp.stale.deliver_btn": "Выдать вручную",
  "mp.stale.cancel_btn": "Отменить навсегда",
  "mp.stale.slow": "Долго обрабатывается — проверьте заказ позже.",
  "mp.stale.slow_title": "Долгая обработка",
  "mp.stale.start_failed": "Не удалось запустить.",
  "mp.common.error_title": "Ошибка",

  // orders section
  "mp.orders.f.product": "Товар",
  "mp.orders.f.product_ph": "название из {mp}",
  "mp.orders.f.variant": "Вариант",
  "mp.orders.f.variant_pick": "сначала выберите товар",
  "mp.orders.f.variant_none": "вариантов нет",
  "mp.orders.f.variant_any": "любой",
  "mp.orders.f.status": "Статус",
  "mp.orders.st.all": "все",
  "mp.orders.st.delivered": "Выполнен",
  "mp.orders.st.pending": "В процессе",
  "mp.orders.st.error": "Ошибка выдачи",
  "mp.orders.st.stale": "Не выдан: старый заказ",
  "mp.orders.st.cancelled": "Отменён",
  "mp.orders.search": "Найти",
  "mp.orders.reset": "Сброс",
  "mp.orders.f.invoice": "№ заказа {mp}",
  "mp.orders.f.invoice_ph": "например 292149981",
  "mp.orders.f.uniquecode": "Уникальный код",
  "mp.orders.f.email": "Email покупателя",
  "mp.orders.loading": "Загружаем заказы…",
  "mp.orders.none_filter": "По вашему фильтру ничего не найдено.",
  "mp.orders.none": "Заказов с {mp} пока нет.",
  "mp.orders.col.product": "Товар ({mp})",
  "mp.orders.col.order_no": "Заказ №",
  "mp.orders.col.date": "Дата",
  "mp.orders.col.buyer": "Покупатель",
  "mp.orders.col.variant": "Вариант",
  "mp.orders.col.status": "Статус",
  "mp.orders.from": "Заказ с {mp}",
  "mp.orders.code_label": "Код:",
  "mp.order.cancelled_title": "Вы отменили этот заказ",
  "mp.order.cancelled_desc":
    "Покупатель видит сообщение об отмене и не может получить товар по этой ссылке.",
  "mp.order.refunded_title": "Возврат на площадке",
  "mp.order.refunded_desc":
    "По этому заказу оформлен возврат на стороне площадки (уникальный код больше не действует). Покупатель видит сообщение о возврате. Если коды уже были выданы — отозвать их нельзя, это просто пометка для вас.",
  "mp.order.topup_title": "Пополнение Steam",
  "mp.order.topup_requested": "Заказано покупателем:",
  "mp.order.topup_login_in": "Логин в {mp}:",
  "mp.order.topup_changed_seller": "вы сменили вручную",
  "mp.order.topup_changed_buyer": "покупатель сменил",
  "mp.order.topup_topped": ", пополнено на:",
  "mp.order.topup_login": "Логин:",
  "mp.order.paid_here": "Заплачено у нас:",
  "mp.order.codes_count": "Кодов выдано:",
  "mp.order.copy_all": "Копировать все коды",
  "mp.order.codes_copied": "Коды скопированы",
  "mp.order.service_fallback": "услуга #{id}",
  "mp.order.no_codes": "Коды ещё не выданы.",
  "mp.order.pages": "Страница {page} из {pages} · всего заказов: {total}",
  "mp.prev": "← Назад",
  "mp.next": "Вперёд →",

  // product row
  "mp.prow.slow_title": "Долгая обработка",
  "mp.prow.slow_body": "Площадка ещё обрабатывает — проверьте после синхронизации.",
  "mp.prow.enabled_done": "Товар включён на площадке",
  "mp.prow.disabled_done": "Товар отключён на площадке",
  "mp.prow.status_failed": "Не удалось сменить статус",
  "mp.prow.status_failed_body": "У нас ничего не изменилось.",
  "mp.prow.no_title": "(без названия)",
  "mp.prow.enabled": "В продаже",
  "mp.prow.disabled": "Отключён",
  "mp.prow.enable": "Включить",
  "mp.prow.disable": "Отключить",

  // instruction editor
  "mp.instr.title": "Инструкция для покупателя (этот товар)",
  "mp.instr.subtitle":
    "Показывается покупателю над кодами на странице выдачи (как активировать и т.п.). Оставьте пустым — блок не показывается.",
  "mp.instr.ph": "Напр.: Активируйте код в Steam → Пополнить кошелёк…",
  "mp.instr.saved": "Инструкция сохранена",
  "mp.instr.save": "Сохранить инструкцию",

  // auto-hide toggle
  "mp.autohide.title": "Авто-скрытие, когда нет в наличии",
  "mp.autohide.desc":
    "Если у привязанной услуги кончится сток — вариант автоматически скрывается на площадке (если это основной — основным становится самый дешёвый из доступных, цены пересчитываются). Все варианты без стока — весь товар отключается. Сток вернулся — всё возвращается само. Проверка примерно каждые 30 сек.",
  "mp.autohide.on": "Авто-скрытие включено",
  "mp.autohide.off": "Авто-скрытие выключено",

  // delivery-kind editor
  "mp.dk.type": "Тип выдачи:",
  "mp.dk.codes": "Коды",
  "mp.dk.topup": "Пополнение Steam",
  "mp.dk.type_topup_toast": "Тип: пополнение Steam",
  "mp.dk.type_codes_toast": "Тип: коды",
  "mp.dk.topup_intro":
    "Логин покупатель вводит в опции формы на площадке (укажите её ниже). Сумма берётся из количества в заказе на площадке (cnt_goods) в выбранной валюте — мы переводим её в USD (тем же курсом, что страница пополнения Steam на сайте) и пополняем аккаунт через выбранную услугу.",
  "mp.dk.currency_label": "Валюта суммы на площадке",
  "mp.dk.ccy_usd": "USD (без конвертации)",
  "mp.dk.our_service": "Наша услуга пополнения Steam (списывается с баланса):",
  "mp.dk.service_fallback": "услуга",
  "mp.dk.not_picked": "не выбрана",
  "mp.dk.search_ph": "Имя, категория, меню или #ID…",
  "mp.dk.search_title":
    "Подсказки: «#3 » (с пробелом) — ровно id 3; «#3» (без пробела) — id, начинающиеся на 3; обычный текст — поиск по имени / категории / меню.",
  "mp.dk.login_option": "Опция на площадке = логин Steam",
  "mp.dk.pick_option": "— выберите опцию —",
  "mp.dk.no_options":
    "У товара нет опций на площадке — проверьте, что форма товара настроена (логин/сумма как параметры).",
  "mp.dk.fill_fields": "Заполните поля",
  "mp.dk.fill_fields_body": "Нужны услуга и опция логина.",
  "mp.dk.topup_saved": "Настройки пополнения сохранены",
  "mp.dk.save_topup": "Сохранить настройки пополнения",

  // substitutions
  "mp.subs.loading": "Загружаем замены…",
  "mp.subs.title": "Замены номиналов",
  "mp.subs.intro":
    "Если номинала нет в наличии, его можно выдать автоматически другими номиналами этого же товара. Пример: «1 × $20 ⇒ 2 × $10». Правила работают по цепочке: при «$20 ⇒ 2 × $10» и «$10 ⇒ 2 × $5» заказ на $20 при наличии только $5 уйдёт как 4 × $5. Пока хотя бы одна цепочка покрывает заказ, вариант не скрывается; на выдаче мы создаём отдельные заказы на номиналы-заменители (по обычной цене сервиса). Циклические правила (A→B и B→A) сервер не сохранит.",
  "mp.subs.need_mappings":
    "Сначала привяжите варианты к услугам — без привязок нечего подменять.",
  "mp.subs.none": "Правил замены пока нет.",
  "mp.subs.edit": "Изменить",
  "mp.subs.if_oos": "Если нет в наличии:",
  "mp.subs.pick_denom": "— выберите номинал —",
  "mp.subs.remove_rule": "Удалить правило",
  "mp.subs.replace_with": "заменять на (на каждую 1 шт. отсутствующего):",
  "mp.subs.service": "— услуга —",
  "mp.subs.qty": "× количество:",
  "mp.subs.min_one_part": "Должна быть хотя бы одна часть",
  "mp.subs.remove": "Убрать",
  "mp.subs.add_part": "+ Добавить часть",
  "mp.subs.add_rule": "+ Добавить правило",
  "mp.subs.save": "Сохранить замены",
  "mp.subs.saved": "Замены сохранены",
  "mp.subs.saved_body": "правил(а): {n}",
  "mp.subs.not_saved": "Не сохранено",
  "mp.subs.save_error": "Ошибка сохранения",
  "mp.subs.v.target": "Выберите номинал-цель для каждого правила.",
  "mp.subs.v.dup_rule": "Дубликат правила для номинала «{name}».",
  "mp.subs.v.no_parts": "У правила для «{name}» нет частей замены.",
  "mp.subs.v.part_service": "Выберите услугу для каждой части замены.",
  "mp.subs.v.part_same":
    "Часть замены не может совпадать с её же номиналом «{name}».",
  "mp.subs.v.dup_part": "Дубликат части «{p}» в правиле «{name}».",
  "mp.subs.v.qty": "Количество в части должно быть 1–1000.",

  // mapping editor
  "mp.map.loading_variants": "Загружаем варианты…",
  "mp.map.variants_failed":
    "Не удалось получить варианты товара с площадки (токен / прокси / ответ API). Повторите позже.",
  "mp.map.title": "Привязки вариантов к услугам",
  "mp.map.collapse_all": "Свернуть все",
  "mp.map.expand_all": "Развернуть все",
  "mp.map.orphans_title":
    "Привязки к вариантам, которых больше нет на площадке",

  // variant block
  "mp.var.default": "основной",
  "mp.var.hidden": "скрыт",
  "mp.var.no_links": "нет привязок",
  "mp.var.n_links": "привязок: {n}",
  "mp.var.make_default": "Сделать основным",
  "mp.var.make_default_title":
    "Сделать этот вариант основным: его цена станет базой товара, надбавки остальных пересчитаются, итоговые цены для покупателей не изменятся",
  "mp.var.return_title": "Вернуть вариант в продажу (виден на площадке)",
  "mp.var.hide_title":
    "Снять с продажи: скрыт на площадке + мы перестаём его выдавать",
  "mp.var.return": "Вернуть в продажу",
  "mp.var.hide": "Скрыть",
  "mp.var.cost_title":
    "Во сколько обойдётся вам: точная цена с вашими условиями × количество, сумма по всем привязкам этого варианта",
  "mp.var.digi_title_src":
    "Цена варианта на площадке: {src} {ccy} ≈ ${usd}; прибыль = (площадка − ваши затраты) / ваши затраты",
  "mp.var.digi_title": "Цена варианта на площадке (USD-эквивалент)",
  "mp.var.key_title": "Ключ варианта — нажмите, чтобы выделить, или нажмите 📋",
  "mp.var.key_copy": "Скопировать ключ варианта",
  "mp.var.hidden_done": "Вариант скрыт на площадке",
  "mp.var.return_done": "Вариант снова в продаже",
  "mp.var.is_default_warn": "Это вариант по умолчанию.",
  "mp.var.is_default_title": "Это вариант по умолчанию",
  "mp.var.is_default_body": "Сначала сделайте основным другой вариант.",
  "mp.var.hide_failed_warn":
    "Не удалось скрыть на площадке: {err}. Вариант остаётся в продаже — попробуйте ещё раз.",
  "mp.var.hide_failed": "Не удалось скрыть",
  "mp.var.hide_failed_body": "Вариант всё ещё продаётся на площадке.",
  "mp.var.error": "ошибка",
  "mp.var.failed": "Не удалось.",

  // promote / reprice flow
  "mp.promote.preparing": "Готовим пересчёт…",
  "mp.promote.slow_calc": "Долго считается. Попробуйте ещё раз.",
  "mp.promote.calc_failed": "Не удалось рассчитать.",
  "mp.promote.calc_failed_title": "Не удалось рассчитать",
  "mp.promote.error": "Ошибка.",
  "mp.promote.applying":
    "Площадка пересчитывает цены… (может занять 1–2 минуты)",
  "mp.promote.slow_apply":
    "Площадка долго не отвечает. Проверьте товар в кабинете площадки и попробуйте ещё раз.",
  "mp.promote.slow_apply_title": "Долгий пересчёт",
  "mp.promote.done": "Готово",
  "mp.promote.done_hide": "Основной сменён, старый скрыт, цены сохранены.",
  "mp.promote.done_nohide": "Вариант стал основным, цены сохранены.",
  "mp.promote.rejected": "Площадка отклонила",
  "mp.promote.recalc_error": "Ошибка пересчёта.",
  "mp.promote.apply_failed": "Не удалось применить",
  "mp.promote.title_hide": "Сменить основной вариант, чтобы скрыть этот",
  "mp.promote.title_make": "Сделать «{label}» основным",
  "mp.promote.desc_hide":
    "Площадка не даёт скрыть основной вариант. Выберите, какой станет основным — его цена станет базой товара, надбавки остальных пересчитаются так, чтобы итоговые цены для покупателей не изменились.",
  "mp.promote.desc_make":
    "Цена этого варианта станет базой товара, надбавки остальных пересчитаются — итоговые цены для покупателей НЕ изменятся. Текущий основной останется в продаже, просто перестанет быть основным.",
  "mp.promote.no_siblings":
    "Нет других вариантов в этой опции — сменить основной не на что.",
  "mp.promote.pick_new": "— выберите новый основной —",
  "mp.promote.calc": "Считаем…",
  "mp.promote.col.variant": "Вариант",
  "mp.promote.col.now": "Сейчас",
  "mp.promote.col.will": "Станет",
  "mp.promote.col.buyer": "Итог покупателю",
  "mp.promote.will_hidden": "скрыт",
  "mp.promote.will_default": "основной (база), {mod}",
  "mp.promote.base": "База товара:",
  "mp.promote.base_after": ". Итоговые цены покупателям не меняются.",
  "mp.promote.applying_btn": "Применяем на площадке…",
  "mp.promote.apply_btn": "Применить на площадке",

  // mapping row
  "mp.maprow.qty_min": "кол-во ≥ 1",
  "mp.maprow.saved": "Сохранено",
  "mp.maprow.cost_title":
    "Стоимость этой привязки для вас (точная, с вашими условиями × количество)",
  "mp.maprow.qty": "кол-во",
  "mp.maprow.del_confirm": "Удалить привязку → {name}?",
  "mp.maprow.del_failed": "Не удалось удалить",

  // add mapping
  "mp.add.pick_service": "Выберите услугу из списка",
  "mp.add.no_links": "Привязок нет. Добавьте услугу ниже.",
  "mp.add.hint":
    "#3 ⇒ ID начинаются на 3 · #3 (с пробелом) ⇒ ровно ID 3",
  "mp.add.added": "Привязка добавлена",
  "mp.add.add_failed": "Не удалось добавить",
  "mp.add.btn": "Добавить",

  // connect form
  "mp.connect.soon": "Скоро. Эта площадка пока недоступна.",
  "mp.connect.fill_all": "Заполните все поля",
  "mp.connect.fill_secret": "Заполните Secret Key",
  "mp.connect.connected": "Аккаунт подключён",
  "mp.connect.failed": "Не удалось подключить",
  "mp.connect.failed_body":
    "Проверьте seller_id, API-ключ и прокси и попробуйте ещё раз.",
  "mp.connect.seller_ph": "seller_id (число)",
  "mp.connect.apikey_ph": "API-ключ",
  "mp.connect.secret_ph": "Secret Key",
  "mp.connect.proxy_ph": "http://user:pass@host:port",
  "mp.connect.proxy_hint":
    "Аккаунт общается с площадкой через этот прокси, поэтому рабочий прокси обязателен. Формат: http://user:pass@host:port или socks5://host:port.",
  "mp.connect.checking": "Проверяем…",
  "mp.connect.btn": "Подключить",

  // pricing panel
  "mp.pricing.anchor_incomplete": "Принудительная цена неполная",
  "mp.pricing.forced_not_number": "Принудительная цена — не число",
  "mp.pricing.markup_not_number": "Наценка — не число",
  "mp.pricing.pin_incomplete": "Закрепление неполное",
  "mp.pricing.pin_need_price": "Выбран вариант — укажите его принудительную цену.",
  "mp.pricing.pin_need_variant": "Указана цена — выберите закреплённый вариант.",
  "mp.pricing.settings_saved": "Настройки сохранены",
  "mp.pricing.calc_failed": "Не удалось посчитать",
  "mp.pricing.slow": "Долгая обработка",
  "mp.pricing.slow_body": "Площадка долго применяет — проверьте через минуту.",
  "mp.pricing.applied": "Цены применены",
  "mp.pricing.apply_failed": "Не удалось применить",
  "mp.pricing.title": "Авто-цены для этого товара",
  "mp.pricing.desc":
    "Можно закрепить один вариант — у него будет ваша принудительная цена в ₽ (он становится демпинг-якорем «от X ₽»). Остальные варианты считаются по формуле: наша цена в USD × курс ₽ × (1 + наценка%). Если якорь скрыт (нет стока) — расчёт всё равно проходит, остальные варианты не ломаются.",
  "mp.pricing.anchor_label": "Закреплённый вариант (его цена = база товара)",
  "mp.pricing.anchor_none": "— не закреплять —",
  "mp.pricing.main_suffix": "  (основной)",
  "mp.pricing.anchor_hint":
    "Обычно это основной вариант на площадке. Если пусто — все варианты идут по формуле.",
  "mp.pricing.forced_label": "Принудительная цена закреплённого варианта (₽)",
  "mp.pricing.forced_ph": "напр. 100",
  "mp.pricing.markup_label": "Наценка по умолчанию (%)",
  "mp.pricing.rules_label": "Правила наценки по вариантам (опционально)",
  "mp.pricing.rule_add": "+ правило",
  "mp.pricing.no_rules": "Правил нет — везде применяется наценка по умолчанию.",
  "mp.pricing.rule_variant_ph": "variant_key (опц.)",
  "mp.pricing.rule_markup_ph": "наценка %",
  "mp.pricing.auto_label": "Авто-обновление примерно каждые 60 сек",
  "mp.pricing.auto_desc":
    "Фоновый цикл следит за нашей ценой и курсом ₽ и при изменении отправляет новые цены на площадку. Включается отдельно от ручной кнопки ниже.",
  "mp.pricing.save": "Сохранить настройки",
  "mp.pricing.preview_btn": "Посчитать предпросмотр",
  "mp.pricing.apply_now_title": "Применить рассчитанные цены на площадке",
  "mp.pricing.apply_now": "Применить сейчас",
  "mp.pricing.applying": "Применяем на площадке…",
  "mp.pricing.last_run": "Последний запуск:",
  "mp.pricing.last_error": "Ошибка:",
  "mp.pricing.rate": "Курс:",
  "mp.pricing.col.variant": "Вариант",
  "mp.pricing.col.our": "наш $",
  "mp.pricing.col.markup": "наценка %",
  "mp.pricing.col.price_rub": "цена ₽",
  "mp.pricing.col.modifier": "модификатор",
  "mp.pricing.anchor_tag": "якорь",
  "mp.pricing.base_inline": " · база площадки: ",
  "mp.pricing.default_inline": " · вариант по умолчанию:",
  "mp.pricing.anchor_inline": " · якорь: ",
};

const DICT: Record<Lang, Record<string, string>> = { en: EN, ru: RU };

/** Look up a translation by key. Falls back to English, then to the key. */
export function t(lang: Lang, key: string): string {
  return DICT[lang][key] ?? EN[key] ?? key;
}

/** React hook bound to the current language. */
export function useT(): (key: string) => string {
  const lang = useLang((s) => s.lang);
  return (key: string) => t(lang, key);
}

/**
 * Interpolating variant of {@link useT}: looks the key up, then
 * substitutes every `{name}` token from `vars`. Lets a translated
 * string carry placeholders (`"No orders from {mp} yet."`) without
 * each call site hand-rolling `.replace`.
 */
export function useTf(): (
  key: string,
  vars?: Record<string, string | number>,
) => string {
  const lang = useLang((s) => s.lang);
  return (key, vars) => {
    let out = t(lang, key);
    if (vars) {
      for (const k of Object.keys(vars)) {
        out = out.replace(
          new RegExp(`\\{${k}\\}`, "g"),
          String(vars[k]),
        );
      }
    }
    return out;
  };
}
